import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation } from "wouter";
import { useGetJournal, useDeleteJournal } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { ArrowLeft, Trash2, CheckCircle2, ChevronDown, ChevronUp, Camera, X, Plus, Pencil, Play, ExternalLink, Headphones, FileText, Download, Mic, MicOff, Share2, Copy, GripVertical, Maximize2, Pipette, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { toPng, toJpeg, toSvg } from "html-to-image";
import { jsPDF } from "jspdf";
import { StickerPanel } from "@/components/sticker-panel";
import {
  StickerOverlay, AnchoredStickers, StaticSticker, isAnchorableSticker, projectToCardEdge, getStickyTextColor,
  anchoredEdgeSides, ANCHOR_EDGE_PAD,
  type PlacedSticker, type StickerDropInfo,
} from "@/components/sticker-overlay";
import { JournalCanvas, unionRects, type JournalCanvasApi, type FocusTarget, type WorldRect } from "@/components/journal-canvas";
import { AttributionPanel } from "@/components/attribution-panel";
import { FloatingToolbar, FONTS, type FontKey } from "@/components/floating-toolbar";
import { VerticalCardStack, type StackItem } from "@/components/vertical-card-stack";
import { SketchCanvas, type Stroke, type SketchRect } from "@/components/sketch-canvas";
import type { StickerItem } from "@/lib/sticker-library";
import { authFetch } from "@/lib/auth-fetch";
import { cleanSocialSourceTitle } from "@/lib/decode-html-entities";
import { apiUrl } from "@/lib/api-base";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useCanvasLayoutSync } from "@/hooks/use-canvas-layout-sync";
import { coerceJournalTagForDisplay, type JournalLibraryTag } from "@/lib/journal-tags";

// ─── Decorative SVG elements ─────────────────────────────────────────────────

function FlowerBloom({ className, color = "#8b5cf6" }: { className?: string; color?: string }) {
  const petals = color + "40";
  return (
    <svg viewBox="0 0 80 80" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="40" cy="40" r="8" fill={color} opacity="0.5" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <ellipse
          key={deg}
          cx={40 + 18 * Math.cos((deg * Math.PI) / 180)}
          cy={40 + 18 * Math.sin((deg * Math.PI) / 180)}
          rx="8" ry="12"
          fill={petals} opacity="0.9"
          transform={`rotate(${deg + 90} ${40 + 18 * Math.cos((deg * Math.PI) / 180)} ${40 + 18 * Math.sin((deg * Math.PI) / 180)})`}
        />
      ))}
      <circle cx="40" cy="40" r="7" fill={color} opacity="0.55" />
    </svg>
  );
}

function StarDoodle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 4l3 10h10l-8 6 3 10-8-6-8 6 3-10-8-6h10z" fill="#fde68a" stroke="#f59e0b" strokeWidth="1" opacity="0.8" />
    </svg>
  );
}

function TapeStrip({ className, color = "#fde68a" }: { className?: string; color?: string }) {
  return (
    <div className={cn("absolute w-16 h-5 opacity-60 rounded-sm", className)}
      style={{ background: color, transform: "rotate(-3deg)" }} />
  );
}

function PaperPin({ className }: { className?: string }) {
  return (
    <div className={cn("absolute w-4 h-4 rounded-full shadow-md z-10", className)}
      style={{ background: "radial-gradient(circle at 35% 35%, #e11d48, #9f1239)", border: "2px solid #be123c" }} />
  );
}

function SketchArrow({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 30" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 18 Q20 8 48 15" stroke="#78716c" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2" />
      <path d="M44 10 L50 16 L42 18" stroke="#78716c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

function WashiTape({ className, color = "#bbf7d0" }: { className?: string; color?: string }) {
  return (
    <div className={cn("absolute h-7 opacity-50", className)}
      style={{ background: `repeating-linear-gradient(90deg, ${color} 0px, ${color} 12px, transparent 12px, transparent 16px)`, minWidth: "80px" }} />
  );
}

// ─── Illustration sticker ─────────────────────────────────────────────────────

function IllustrationSticker({
  src,
  rotation = 0,
  size = 100,
  seed = 1,
  className,
  onReplace,
}: {
  src?: string;
  rotation?: number;
  size?: number;
  seed?: number;
  className?: string;
  onReplace?: (file: File) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actualSrc = (!src || failed)
    ? `https://picsum.photos/seed/s${seed}/200/200`
    : src;

  return (
    <div
      className={cn("relative inline-block group", className)}
      style={{ transform: `rotate(${rotation}deg)`, width: size, height: size }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!loaded && (
        <div className="absolute inset-0 rounded-lg bg-stone-100 animate-pulse opacity-50" />
      )}
      <img
        src={actualSrc}
        alt=""
        onLoad={() => setLoaded(true)}
        onError={() => { if (!failed) { setFailed(true); setLoaded(false); } }}
        className={cn(
          "w-full h-full object-cover rounded-lg transition-opacity duration-500",
          loaded ? "opacity-100" : "opacity-0"
        )}
        style={{ filter: "saturate(0.85) contrast(1.05)", boxShadow: "2px 3px 12px rgba(0,0,0,0.14)" }}
      />
      {onReplace && hovered && (
        <button
          className="absolute inset-0 rounded-lg flex flex-col items-center justify-center gap-1 transition-all"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          title="Replace image"
        >
          <Camera className="w-5 h-5 text-white drop-shadow" />
          <span className="text-white text-[9px] font-sans font-semibold tracking-wide drop-shadow">Replace</span>
        </button>
      )}
      {onReplace && (
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplace(f); e.target.value = ""; }} />
      )}
    </div>
  );
}

// ─── Photo frame (Polaroid) ───────────────────────────────────────────────────

function PolaroidPhoto({
  src,
  caption,
  rotation = 0,
  seed = 1,
  className,
  onReplace,
}: {
  src?: string;
  caption?: string;
  rotation?: number;
  seed?: number;
  className?: string;
  onReplace?: (file: File) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actualSrc = (!src || failed) ? `https://picsum.photos/seed/${seed + 42}/600/400` : src;

  return (
    <div className={cn("inline-block", className)} style={{ transform: `rotate(${rotation}deg)` }}>
      <div className="bg-white p-2 pb-6 shadow-[2px_4px_16px_rgba(0,0,0,0.18)] relative">
        <div
          className="w-full aspect-[4/3] overflow-hidden relative"
          style={{ background: "linear-gradient(135deg, #e8e0d4 0%, #d4cbc0 100%)" }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-2 border-stone-300 border-t-transparent animate-spin opacity-40" />
            </div>
          )}
          <img
            src={actualSrc}
            alt={caption ?? "Journal image"}
            onLoad={() => setLoaded(true)}
            onError={() => { if (!failed) { setFailed(true); setLoaded(false); } }}
            className={cn("w-full h-full object-cover transition-opacity duration-700", loaded ? "opacity-100" : "opacity-0")}
          />
          {onReplace && hovered && (
            <button
              className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 transition-all"
              style={{ background: "rgba(0,0,0,0.40)" }}
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              title="Replace photo"
            >
              <Camera className="w-6 h-6 text-white drop-shadow" />
              <span className="text-white text-[10px] font-sans font-semibold tracking-wider drop-shadow">Replace photo</span>
            </button>
          )}
        </div>
        {caption && (
          <p className="text-center text-xs mt-2 text-stone-500 leading-tight px-1"
            style={{ fontFamily: "'Kalam', cursive", fontWeight: 400 }}>
            {caption}
          </p>
        )}
      </div>
      {onReplace && (
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onReplace(f); e.target.value = ""; }} />
      )}
    </div>
  );
}

// ─── Sticky note ─────────────────────────────────────────────────────────────

function StickyNote({
  children,
  color = "#fef9c3",
  rotation = 0,
  label,
  className,
}: {
  children: React.ReactNode;
  color?: string;
  rotation?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative p-5 shadow-md", className)}
      style={{
        background: color,
        transform: `rotate(${rotation}deg)`,
        boxShadow: "2px 4px 14px rgba(0,0,0,0.10), inset 0 -2px 0 rgba(0,0,0,0.04)",
      }}
    >
      {label && (
        <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-2">{label}</p>
      )}
      <div className="absolute top-0 left-0 right-0 h-1 opacity-30"
        style={{ background: "linear-gradient(to right, transparent, rgba(0,0,0,0.08), transparent)" }} />
      {children}
    </div>
  );
}

// ─── Scrapbook stamp ─────────────────────────────────────────────────────────

function JournalStamp({ tag, date }: { tag: string; date: string }) {
  const key = coerceJournalTagForDisplay(tag);
  const colors: Record<JournalLibraryTag, { accent: string; text: string; bg: string }> = {
    Health: { accent: "#10b981", text: "#0a4f3a", bg: "rgba(16,185,129,0.07)" },
    Finance: { accent: "#f59e0b", text: "#6b3f08", bg: "rgba(245,158,11,0.07)" },
    "Self-care": { accent: "#8b5cf6", text: "#3b1f6e", bg: "rgba(139,92,246,0.07)" },
    "Self-growth": { accent: "#6366f1", text: "#2d2f7a", bg: "rgba(99,102,241,0.07)" },
    Tech: { accent: "#3b82f6", text: "#1e3a5f", bg: "rgba(59,130,246,0.07)" },
    Other: { accent: "#78716c", text: "#44403c", bg: "rgba(120,113,108,0.07)" },
  };
  const c = colors[key];

  return (
    <div
      className="inline-flex flex-col items-center px-5 py-3 rounded-lg"
      style={{
        background: c.bg,
        border: `1px solid ${c.accent}20`,
        transform: "rotate(-2deg)",
        backdropFilter: "blur(8px)",
      }}
    >
      <span
        className="text-[11px] font-semibold uppercase tracking-[0.2em]"
        style={{ color: c.text, fontFamily: "'DM Sans', sans-serif" }}
      >
        {key}
      </span>
      <div className="w-5 h-px mx-auto my-1.5" style={{ background: `${c.accent}30` }} />
      <span
        className="text-[10px] tracking-wide"
        style={{ color: c.text, opacity: 0.5, fontFamily: "'DM Sans', sans-serif" }}
      >
        {date}
      </span>
    </div>
  );
}

// ─── Action checkbox (clean body copy) ───────────────────────────────────────

function ActionItem({
  done,
  onToggle,
  children,
}: {
  done: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <button onClick={onToggle} className="flex items-start gap-3.5 text-left group w-full py-3">
      <div className={cn(
        "mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all",
        done ? "bg-stone-700 border-stone-700" : "border-stone-300 group-hover:border-stone-500"
      )}>
        {done && (
          <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none">
            <path d="M2.5 6l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <span className={cn(
        "text-sm leading-relaxed font-sans transition-all",
        done ? "line-through text-stone-400" : "text-stone-700"
      )}>
        {children}
      </span>
    </button>
  );
}

// ─── Source content widget ───────────────────────────────────────────────────

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/,
    /youtube\.com\/embed\/([\w-]{11})/,
    /youtube\.com\/v\/([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function SourceWidget({
  sourceUrl,
  sourceTitle,
  sourceType,
  accentColor,
}: {
  sourceUrl?: string | null;
  sourceTitle?: string | null;
  sourceType?: string | null;
  accentColor: string;
}) {
  const displayTitle = sourceTitle ? cleanSocialSourceTitle(sourceTitle) : null;
  const videoId = sourceUrl ? extractYouTubeId(sourceUrl) : null;
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;

  // YouTube video with URL — show clickable thumbnail with play overlay
  if (videoId && thumbnailUrl) {
    return (
      <a
        href={sourceUrl!}
        target="_blank"
        rel="noopener noreferrer"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        className="group bg-white border border-[#e4ddd4] rounded-2xl overflow-hidden block shadow-sm select-none"
      >
        <div className="aspect-video w-full relative bg-stone-100">
          <img
            src={thumbnailUrl}
            alt={displayTitle ?? "Source video"}
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
            draggable={false}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-sm shadow-lg transition-transform group-hover:scale-110"
              style={{ background: "rgba(255,255,255,0.9)" }}
            >
              <Play className="w-5 h-5 ml-0.5" style={{ color: accentColor }} fill={accentColor} />
            </div>
          </div>
        </div>
        {displayTitle && (
          <div className="px-4 py-3 flex items-center gap-2">
            <Play className="w-3.5 h-3.5 shrink-0" style={{ color: accentColor }} />
            <p className="text-[12px] text-stone-600 font-sans truncate">{displayTitle}</p>
          </div>
        )}
      </a>
    );
  }

  // Non-YouTube link with URL — show a clickable link card
  if (sourceUrl) {
    const isPodcast = sourceType === "playbook" || /podcast|spotify|apple.*podcast|overcast/i.test(sourceUrl);
    const Icon = isPodcast ? Headphones : ExternalLink;
    const label = isPodcast ? "Listen to source" : "View source";

    return (
      <a
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        className="group bg-white border border-[#e4ddd4] rounded-2xl p-4 flex items-center gap-3 transition-all hover:border-stone-400 hover:shadow-sm select-none"
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
          style={{ background: accentColor + "18" }}
        >
          <Icon className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div className="flex-1 min-w-0">
          {displayTitle && (
            <p className="text-[13px] text-stone-700 font-sans font-medium truncate">{displayTitle}</p>
          )}
          <p className="text-[11px] text-stone-400 font-sans truncate">{label}</p>
        </div>
        <ExternalLink className="w-3.5 h-3.5 text-stone-300 group-hover:text-stone-500 transition-colors shrink-0" />
      </a>
    );
  }

  // Fallback — no URL, just show the title if available
  if (!displayTitle) return null;

  return (
    <div className="flex items-center gap-2 py-2">
      <FileText className="w-3.5 h-3.5 shrink-0 text-stone-400" />
      <p className="text-[13px] text-stone-600 font-sans font-medium">{displayTitle}</p>
    </div>
  );
}

// ─── Dot-grid paper background ───────────────────────────────────────────────

const PAPER_STYLE: React.CSSProperties = {
  background: "#eeeeec",
  backgroundImage:
    "radial-gradient(circle, rgba(150,147,142,0.22) 1px, transparent 1px), " +
    "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.5), transparent 60%)",
  backgroundSize: "22px 22px, 100% 100%",
};

// ─── Tag to mood color palette ────────────────────────────────────────────────

const TAG_PALETTE: Record<JournalLibraryTag, { accent: string; stickyA: string; stickyB: string; washi: string }> = {
  Health: { accent: "#10b981", stickyA: "#d1fae5", stickyB: "#fef9c3", washi: "#bbf7d0" },
  Finance: { accent: "#f59e0b", stickyA: "#fef9c3", stickyB: "#ffedd5", washi: "#fde68a" },
  "Self-care": { accent: "#8b5cf6", stickyA: "#ede9fe", stickyB: "#ddd6fe", washi: "#ddd6fe" },
  "Self-growth": { accent: "#6366f1", stickyA: "#eef2ff", stickyB: "#e0e7ff", washi: "#c7d2fe" },
  Tech: { accent: "#3b82f6", stickyA: "#dbeafe", stickyB: "#ede9fe", washi: "#bfdbfe" },
  Other: { accent: "#78716c", stickyA: "#f5f5f4", stickyB: "#e7e5e4", washi: "#d6d3d1" },
};

const COLOR_THEMES = [
  { tag: "Health", label: "Health", dot: "#10b981" },
  { tag: "Finance", label: "Finance", dot: "#f59e0b" },
  { tag: "Self-care", label: "Self-care", dot: "#8b5cf6" },
  { tag: "Self-growth", label: "Self-growth", dot: "#6366f1" },
  { tag: "Tech", label: "Tech", dot: "#3b82f6" },
  { tag: "Other", label: "Other", dot: "#78716c" },
] as const;

function getpalette(tag: string) {
  return TAG_PALETTE[coerceJournalTagForDisplay(tag)];
}

function downloadDataUrl(dataUrl: string, fileName: string) {
  const link = document.createElement("a");
  link.download = fileName;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function stripMd(text: string | undefined | null): string {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/__(.*?)__/g, "$1");
}

// Illustration placements: [rotation, size, position-class]
const ILLUS_SLOTS: Array<{ rotation: number; size: number; floatClass: string }> = [
  { rotation: 8,   size: 96,  floatClass: "absolute -right-4 -top-6 z-10" },
  { rotation: -7,  size: 88,  floatClass: "absolute -right-2 -bottom-4 z-10" },
  { rotation: 11,  size: 80,  floatClass: "absolute -left-3 -top-4 z-10" },
  { rotation: -5,  size: 104, floatClass: "absolute -left-5 -bottom-3 z-10" },
  { rotation: 6,   size: 84,  floatClass: "absolute right-2 -bottom-6 z-10" },
  { rotation: -9,  size: 92,  floatClass: "absolute left-4 -top-5 z-10" },
];

// ─── Voice mic button for writing areas ──────────────────────────────────────

function VoiceMicButton({ onTranscript }: { onTranscript: (text: string) => void }) {
  const voice = useVoiceInput({ onTranscript });
  if (!voice.isSupported) return null;
  return (
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); voice.toggle(); }}
      className={cn(
        "absolute bottom-2 right-2 transition-all z-10",
        voice.isListening
          ? "text-primary animate-pulse"
          : "text-stone-500 hover:text-stone-700",
      )}
      title={voice.isListening ? "Stop dictation" : "Dictate"}
    >
      {voice.isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}

// ─── Inline editable text (single-line) ──────────────────────────────────────

function InlineEditText({
  value,
  onChange,
  placeholder,
  className,
  style,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); if (draft !== value) onChange(draft); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        placeholder={placeholder}
        className={cn("bg-transparent border-b border-stone-300 focus:border-stone-500 outline-none w-full", className)}
        style={style}
        autoFocus
      />
    );
  }

  return (
    <span
      className={cn("group/ie relative cursor-text inline-flex items-center gap-1", className)}
      style={style}
      onClick={() => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 10); }}
      title="Click to edit"
    >
      {value || <span className="text-stone-500 opacity-80">{placeholder}</span>}
      <Pencil className="w-3 h-3 opacity-0 group-hover/ie:opacity-40 text-stone-500 shrink-0 transition-opacity" />
    </span>
  );
}

// ─── Inline editable textarea (multi-line) ────────────────────────────────────

function InlineEditTextarea({
  value,
  onChange,
  placeholder,
  className,
  style,
  minRows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  minRows?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  if (editing) {
    return (
      <div className="relative">
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { setEditing(false); if (draft !== value) onChange(draft); }}
          onKeyDown={(e) => { if (e.key === "Escape") { e.currentTarget.blur(); } e.stopPropagation(); }}
          onMouseDown={(e) => e.stopPropagation()}
          placeholder={placeholder}
          rows={minRows}
          className={cn("bg-transparent border border-stone-300 focus:border-stone-500 outline-none w-full rounded px-1 pr-10 resize-none", className)}
          style={style}
          autoFocus
        />
        {/* <VoiceMicButton onTranscript={(text) => { setDraft((d) => d ? d + " " + text : text); }} /> */}
      </div>
    );
  }

  return (
    <div
      className={cn("group/ie relative cursor-text", className)}
      style={style}
      onClick={() => { setEditing(true); setTimeout(() => taRef.current?.focus(), 10); }}
      title="Click to edit"
    >
      {value || <span className="text-stone-500 opacity-80">{placeholder}</span>}
      <Pencil className="absolute top-0 right-0 w-3.5 h-3.5 opacity-0 group-hover/ie:opacity-30 text-stone-500 transition-opacity" />
    </div>
  );
}

// ─── Section wrapper with delete button ───────────────────────────────────────

function DeletableSection({
  id,
  hidden,
  onHide,
  children,
  className,
}: {
  id: string;
  hidden: boolean;
  onHide: (id: string) => void;
  children: React.ReactNode;
  className?: string;
}) {
  if (hidden) return null;
  return (
    <div className={cn("group/del relative", className)}>
      <button
        className="absolute -top-2 -right-2 z-30 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md opacity-0 group-hover/del:opacity-100 transition-opacity hover:bg-red-600"
        title="Remove this section"
        onClick={() => onHide(id)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <X className="w-3 h-3" />
      </button>
      {children}
    </div>
  );
}

// ─── Scrapbook calendar widget (torn notebook page, pinned) ───────────────────

function ScrapCalendar({ date, onPickDate }: { date: Date; onPickDate?: (d: Date) => void }) {
  const [view, setView] = useState(new Date(date.getFullYear(), date.getMonth(), 1));
  const year = view.getFullYear();
  const month = view.getMonth();
  const startDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells: { d: number; muted: boolean; today: boolean }[] = [];
  for (let i = startDow - 1; i >= 0; i--) cells.push({ d: prevDays - i, muted: true, today: false });
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ d, muted: false, today: d === date.getDate() && month === date.getMonth() && year === date.getFullYear() });
  }
  while (cells.length % 7 !== 0) cells.push({ d: cells.length - (startDow + daysInMonth) + 1, muted: true, today: false });
  const monthLabel = view.toLocaleString("en-US", { month: "short", year: "numeric" });

  return (
    <div className="relative select-none" style={{ transform: "rotate(-1.5deg)" }}>
      {/* pushpin */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full z-10"
        style={{ background: "radial-gradient(circle at 35% 30%, #f87171, #b91c1c)", boxShadow: "0 2px 3px rgba(0,0,0,0.3)" }} />
      <div className="nb-card rounded-md border border-[#e6e2da] pl-6 pr-3 py-2 w-full max-w-[195px]">
        {/* spiral holes */}
        <div className="absolute left-2 top-3 bottom-3 flex flex-col justify-between">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="w-2 h-2 rounded-full bg-[#eeeeec] border border-[#d8d4cc]" />
          ))}
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <button onClick={() => setView(new Date(year, month - 1, 1))} className="w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-700 text-sm">‹</button>
          <span className="font-['Kalam'] font-bold text-[13px] text-stone-700">{monthLabel}</span>
          <button onClick={() => setView(new Date(year, month + 1, 1))} className="w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-700 text-sm">›</button>
        </div>
        <div className="grid grid-cols-7 gap-y-0.5 text-center">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <span key={i} className="text-[8px] font-bold text-stone-400 font-sans">{d}</span>
          ))}
          {cells.map((c, i) => (
            <button
              key={i}
              onClick={() => !c.muted && onPickDate?.(new Date(year, month, c.d))}
              className={cn(
                "text-[10px] font-sans h-[15px] w-[15px] mx-auto flex items-center justify-center rounded-full transition-colors",
                c.muted ? "text-stone-300" : "text-stone-600 hover:bg-stone-100",
                c.today && "text-white font-bold"
              )}
              style={c.today ? { background: "#ef4d63" } : undefined}
            >
              {c.d}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Rich text (contenteditable) + select-to-color ───────────────────────────

function stripHtml(html: string): string {
  const d = document.createElement("div");
  d.innerHTML = html ?? "";
  return d.textContent ?? "";
}
function escapeHtml(s: string): string {
  const d = document.createElement("div");
  d.textContent = s ?? "";
  return d.innerHTML;
}

const RICH_COLORS = ["#1c1917", "#ffffff", "#ef4d63", "#7c3aed", "#2563eb", "#059669", "#d97706", "#db2777", "#0891b2"];

// A contenteditable region. `value` is HTML; emits HTML on edit. innerHTML is only
// re-synced when the value changes from the outside, so the caret never jumps.
function RichText({
  value, onChange, className, style, placeholder, singleLine, onFocus,
}: {
  value: string; onChange: (html: string) => void; className?: string;
  style?: React.CSSProperties; placeholder?: string; singleLine?: boolean; onFocus?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.innerHTML !== (value || "")) el.innerHTML = value || "";
  }, [value]);
  const emit = () => { const el = ref.current; if (el) onChange(el.innerHTML); };
  return (
    <div
      ref={ref}
      data-richtext
      contentEditable
      suppressContentEditableWarning
      onInput={emit}
      onBlur={emit}
      onFocus={onFocus}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (singleLine && e.key === "Enter") { e.preventDefault(); (e.currentTarget as HTMLElement).blur(); }
        e.stopPropagation();
      }}
      data-ph={placeholder}
      className={cn(
        "outline-none focus:outline-none cursor-text whitespace-pre-wrap",
        "empty:before:content-[attr(data-ph)] empty:before:text-stone-500 empty:before:opacity-80 empty:before:pointer-events-none",
        className,
      )}
      style={style}
    />
  );
}

// Floating colour bar shown whenever text is selected inside any RichText region.
function SelectionColorBar() {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const savedRange = useRef<Range | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const update = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) { setRect(null); savedRange.current = null; return; }
      const range = sel.getRangeAt(0);
      const anc = range.commonAncestorContainer;
      const el = anc.nodeType === 3 ? anc.parentElement : (anc as Element);
      if (!el || !el.closest("[data-richtext]")) { setRect(null); return; }
      const r = range.getBoundingClientRect();
      if (!r || (r.width === 0 && r.height === 0)) { setRect(null); return; }
      savedRange.current = range.cloneRange();
      setRect(r);
    };
    const hide = () => setRect(null);
    document.addEventListener("selectionchange", update);
    window.addEventListener("scroll", hide, true);
    return () => { document.removeEventListener("selectionchange", update); window.removeEventListener("scroll", hide, true); };
  }, []);
  if (!rect) return null;
  const restore = () => {
    const sel = window.getSelection();
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
  };
  const apply = (color: string | null) => {
    restore();
    document.execCommand("styleWithCSS", false, "true");
    if (color) document.execCommand("foreColor", false, color);
    else document.execCommand("removeFormat", false, "");
  };
  const pickFromScreen = async () => {
    const ED = (window as unknown as { EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper;
    if (!ED) return;
    try { const res = await new ED().open(); if (res?.sRGBHex) apply(res.sRGBHex); } catch { /* cancelled */ }
  };
  const hasED = typeof (window as unknown as { EyeDropper?: unknown }).EyeDropper !== "undefined";
  const top = Math.max(8, rect.top - 46);
  const left = Math.min(Math.max(rect.left + rect.width / 2, 190), window.innerWidth - 190);
  return createPortal(
    <div
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: "fixed", top, left, transform: "translateX(-50%)", zIndex: 80,
        background: "rgba(255,255,255,0.78)", backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)", border: "1px solid rgba(255,255,255,0.7)",
        boxShadow: "0 8px 26px rgba(28,25,23,0.22)",
      }}
      className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5"
    >
      {RICH_COLORS.map((c) => (
        <button key={c} onMouseDown={(e) => { e.preventDefault(); apply(c); }}
          title={c} className="w-5 h-5 rounded-full ring-1 ring-black/15 hover:scale-110 transition-transform"
          style={{ background: c }} />
      ))}
      <span className="w-px h-4 bg-stone-300 mx-0.5" />
      {/* Custom colour swatch */}
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => colorInputRef.current?.showPicker?.() ?? colorInputRef.current?.click()}
        title="Custom colour"
        className="w-5 h-5 rounded-full ring-1 ring-black/15 hover:scale-110 transition-transform"
        style={{ background: "conic-gradient(#ef4444,#f59e0b,#eab308,#22c55e,#06b6d4,#3b82f6,#a855f7,#ec4899,#ef4444)" }}
      />
      <input ref={colorInputRef} type="color" className="sr-only" onChange={(e) => apply(e.target.value)} />
      {/* Eyedropper */}
      {hasED && (
        <button onMouseDown={(e) => e.preventDefault()} onClick={pickFromScreen} title="Pick colour from screen"
          className="w-6 h-6 rounded-full flex items-center justify-center text-stone-600 hover:bg-white/70 transition-colors">
          <Pipette className="w-3.5 h-3.5" />
        </button>
      )}
      <span className="w-px h-4 bg-stone-300 mx-0.5" />
      <button onMouseDown={(e) => { e.preventDefault(); apply(null); }} title="Clear colour"
        className="w-5 h-5 rounded-full bg-white ring-1 ring-stone-300 flex items-center justify-center text-stone-500 hover:scale-110 transition-transform text-[10px]">✕</button>
    </div>,
    document.body,
  );
}

// ─── Clamped section preview + "See more" → opens full content in a modal ─────

function SectionBody({
  html, placeholder, style, onOpen,
}: { html: string; placeholder: string; style?: React.CSSProperties; onOpen: () => void }) {
  const long = stripHtml(html).trim().length > 140;
  return (
    <div className="cursor-pointer" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
      {html ? (
        <div className="line-clamp-4 whitespace-pre-wrap" style={style} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <p className="opacity-40" style={style}>{placeholder}</p>
      )}
      {long && (
        <button
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="mt-2 text-[11px] font-sans font-semibold hover:underline"
          style={{ color: "#ef4d63" }}
        >
          See more →
        </button>
      )}
    </div>
  );
}

function SectionBodyModal({
  label, value, style, onSave, onClose,
}: { label: string; value: string; style?: React.CSSProperties; onSave: (v: string) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(value);
  const close = () => { if (draft !== value) onSave(draft); onClose(); };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={close}>
      <div className="absolute inset-0 bg-stone-900/30 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-xl nb-card rounded-2xl border border-[#e6e2da] p-6 max-h-[82vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <SectionHeading label={label} />
          <button onClick={close} className="w-8 h-8 -mt-1 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <RichText
          value={draft}
          onChange={setDraft}
          placeholder="Write here…"
          className="min-h-[220px] leading-relaxed text-stone-900"
          style={style}
        />
        <p className="mt-3 text-[11px] text-stone-400 font-sans">Tip: select any text to change its colour.</p>
      </motion.div>
    </div>
  );
}

// ─── Stylized section header (clean, modern — Canva-template vibe) ────────────

const CORAL = "#ef4d63";

function SectionHeading({ label, children }: { label: string; accent?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-start justify-between gap-2">
      <h3 className="font-['Kalam'] font-bold text-[17px] leading-none text-stone-700 whitespace-nowrap">{label}</h3>
      {children}
    </div>
  );
}

// ─── Viewport hook ────────────────────────────────────────────────────────────

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const handler = () => setMatches(m.matches);
    handler();
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

// Default free-move collage positions (web only), tuned for a ~980px canvas.
type CardPos = { x: number; y: number; w: number };
/** The four draggable reflection cards, in reading order. */
const COLLAGE_CARD_KEYS = ["openingReflection", "coreInsight", "whatSurfaced", "reflectionPrompt"] as const;
const COLLAGE_GAP = 28;
const COLLAGE_CARD_MIN = 250;
const COLLAGE_CARD_MAX = 320;
const COLLAGE_TOP = 24;

/**
 * Fallback only — used before the canvas has been measured (and during prerender).
 * Authored to fit the narrowest canvas the collage layout ever runs at (1024px
 * viewport ≈ 983px canvas), so it can never overlap even if measurement never runs.
 */
const COLLAGE_DEFAULTS: Record<string, CardPos> = {
  openingReflection: { x: 0, y: COLLAGE_TOP, w: 300 },
  coreInsight: { x: 328, y: COLLAGE_TOP, w: 300 },
  whatSurfaced: { x: 656, y: COLLAGE_TOP, w: 300 },
  reflectionPrompt: { x: 0, y: 320, w: 300 },
  nextSteps: { x: 328, y: 320, w: 628 },
};

/**
 * Collision-free default positions for a canvas of `width`.
 *
 * The old defaults were absolute pixel offsets authored against a fixed 1440px
 * canvas (Sit With This sat at x=1140), while the canvas itself is `min(1440px, 96vw)`.
 * Below ~1470px viewport the container shrank but the coordinates didn't, so the
 * right-hand cards ran past the edge and landed on top of their neighbours.
 *
 * This packs the four reflection cards into as many columns as actually fit
 * (shortest-column first, using measured heights). Next Steps is not a canvas card —
 * it's the fixed hero panel — so it isn't placed here. Saved user positions still
 * override this, so dragging and saved customization are unaffected.
 */
function computeCollageLayout(
  width: number,
  heights: Record<string, number>,
): Record<string, CardPos> {
  const cols = width >= 1360 ? 4 : width >= 1000 ? 3 : 2;
  const cardW = Math.max(
    COLLAGE_CARD_MIN,
    Math.min(COLLAGE_CARD_MAX, Math.floor((width - (cols - 1) * COLLAGE_GAP) / cols)),
  );
  const gridW = cols * cardW + (cols - 1) * COLLAGE_GAP;
  const originX = Math.max(0, Math.floor((width - gridW) / 2));

  // Row-based rather than shortest-column masonry: a row's y only depends on the
  // rows above it, so the layout is collision-free even on the first pass when
  // heights haven't been measured yet. `Math.max(measured, MIN_ROW)` keeps a row
  // from being under-estimated and letting the next row ride up into it.
  const MIN_ROW = 240;
  const out: Record<string, CardPos> = {};
  let rowY = COLLAGE_TOP;

  for (let i = 0; i < COLLAGE_CARD_KEYS.length; i += cols) {
    const row = COLLAGE_CARD_KEYS.slice(i, i + cols);
    let rowH = MIN_ROW;
    row.forEach((key, c) => {
      out[key] = { x: originX + c * (cardW + COLLAGE_GAP), y: rowY, w: cardW };
      rowH = Math.max(rowH, heights[key] ?? MIN_ROW);
    });
    rowY += rowH + COLLAGE_GAP;
  }

  return out;
}

/**
 * Reference width of the desktop canvas's DEFAULT layout — not a boundary. The canvas world
 * is unbounded (objects live at any positive/negative world px); this only fixes where the
 * generated journal is laid out by default (x 0…1440, as the page looked in a 1440px
 * window) and what a free sticker's xPct is a percentage of. Being a constant, nothing on
 * the canvas moves when the window is resized or opened on another screen size.
 */
const LAYOUT_REFERENCE_WIDTH = 1440;
/** Content column of the default layout (the old `max-w-7xl` page column), centred in the reference width. */
const LAYOUT_CONTENT_WIDTH = 1280;
/** Collage width at the reference width (the old `min(1440px, 96vw)` evaluated at 1440). */
const LAYOUT_COLLAGE_WIDTH = Math.round(LAYOUT_REFERENCE_WIDTH * 0.96);
/** Display names of the generated sections (stable id → label). Labels are for display only. */
const SECTION_TITLES: Record<string, string> = {
  openingReflection: "Opening Reflection",
  coreInsight: "Core Insight",
  whatSurfaced: "What Surfaced",
  reflectionPrompt: "Sit With This",
};
// Canvas is tall enough for a couple of expanded cards; collapsed cards sit compactly at the top.
const COLLAGE_HEIGHT = 560;

// ─── Canvas drag (desktop) ────────────────────────────────────────────────────

/** Unscaled px per rendered px of `el` — 1 normally, 1/zoom inside the desktop canvas. */
function unscaleFactor(el: HTMLElement): number {
  const w = el.getBoundingClientRect().width;
  return w > 0 && el.offsetWidth > 0 ? el.offsetWidth / w : 1;
}

/**
 * Drag start/move/end that stays under the pointer at any canvas zoom. `onDelta` gets the
 * movement in unscaled px; `onEnd` runs only if the pointer actually moved (> 3px), so a
 * plain click is still a click.
 */
function startCanvasDrag(
  e: React.PointerEvent,
  node: HTMLElement,
  onDelta: (dx: number, dy: number) => void,
  onEnd: () => void,
) {
  const k = unscaleFactor(node);
  const sx = e.clientX, sy = e.clientY;
  let moved = false;
  const move = (ev: PointerEvent) => {
    const dx = ev.clientX - sx, dy = ev.clientY - sy;
    if (!moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
    if (!moved) window.getSelection()?.removeAllRanges();
    moved = true;
    onDelta(dx * k, dy * k);
  };
  const up = () => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", up);
    if (moved) onEnd();
  };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", up);
  return () => moved;
}

/**
 * Makes an in-flow canvas object (header, Source) movable on the desktop canvas. The
 * saved position is an offset from where the object naturally renders — the same
 * `cardPos` entry the old (disabled) FloatDrag used for the Source card.
 *
 * `handle="grip"`: only a hover grip starts a drag, so text inside stays editable.
 * `handle="body"`: the whole object drags; the click that ends a drag is swallowed so
 * a link inside doesn't open.
 */
function CanvasDrag({
  pos, onMove, className, children, handle,
}: {
  pos: { x: number; y: number };
  onMove: (p: { x: number; y: number }) => void;
  className?: string;
  children: React.ReactNode;
  handle: "grip" | "body";
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(pos.x);
  const y = useMotionValue(pos.y);
  const dragging = useRef(false);
  const suppressClick = useRef(false);
  useEffect(() => {
    if (dragging.current) return;
    x.set(pos.x);
    y.set(pos.y);
  }, [pos.x, pos.y, x, y]);

  const begin = (e: React.PointerEvent) => {
    const node = nodeRef.current;
    if (!node || e.button !== 0) return;
    if (handle === "body" && (e.target as Element).closest("input, textarea, [contenteditable='true']")) return;
    e.stopPropagation();
    if (handle === "grip") e.preventDefault();
    const ox = x.get(), oy = y.get();
    dragging.current = true;
    const didMove = startCanvasDrag(e, node, (dx, dy) => { x.set(ox + dx); y.set(oy + dy); }, () => {
      suppressClick.current = true;
      onMove({ x: Math.round(x.get()), y: Math.round(y.get()) });
    });
    const release = () => {
      dragging.current = false;
      if (!didMove()) suppressClick.current = false;
      window.removeEventListener("pointerup", release);
    };
    window.addEventListener("pointerup", release);
  };

  return (
    <motion.div
      ref={nodeRef}
      data-canvas-item=""
      className={cn("relative group/cdrag", handle === "body" && "cursor-grab active:cursor-grabbing", className)}
      style={{ x, y }}
      onPointerDown={handle === "body" ? begin : undefined}
      onClickCapture={(e) => {
        if (!suppressClick.current) return;
        suppressClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {handle === "grip" && (
        <button
          type="button"
          data-export-hide
          onPointerDown={begin}
          onClick={(e) => e.stopPropagation()}
          className="absolute left-4 top-4 z-10 opacity-0 group-hover/cdrag:opacity-100 focus-visible:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-stone-500 hover:text-stone-700 touch-none"
          title="Drag to move"
          aria-label="Drag to move"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      {children}
    </motion.div>
  );
}

// ─── Draggable + tuckable section card ────────────────────────────────────────

function CollageCard({
  label, labelNode, pill, accent, collage, pos, onMove, zIndex, onFront,
  collapsed, onToggleCollapse, selected, onSelect, cardClassName, children, dataKey, overlay, dropHint, edgePad,
  anchorId, anchorLabel,
}: {
  label: string;
  /** Stamped on the draggable node so the canvas can measure this card for auto-layout. */
  dataKey?: string;
  labelNode?: React.ReactNode;
  pill?: React.ReactNode;
  accent: string;
  collage: boolean;
  pos: CardPos;
  onMove: (p: { x: number; y: number }) => void;
  zIndex: number;
  onFront: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  selected: boolean;
  onSelect: () => void;
  cardClassName?: string;
  children: React.ReactNode;
  /** Rendered inside the moving card, above its content (attached stickers). */
  overlay?: React.ReactNode;
  /** Shown while a sticker is dragged over this card: it will attach on drop. */
  dropHint?: string | null;
  /** Extra padding on sides carrying attached stickers, keeping text/controls clear of them. */
  edgePad?: React.CSSProperties;
  /**
   * Stable block id that makes this card a sticker attachment target (stored as the
   * sticker's anchorKey). Any card rendering one is compatible — nothing else is hardcoded.
   */
  anchorId?: string;
  anchorLabel?: string;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const x = useMotionValue(pos.x);
  const y = useMotionValue(pos.y);

  // `useMotionValue` only reads its argument on the first render, so without this
  // the card would ignore every later position — including the responsive default
  // layout recomputed on resize. Dragging still wins: the drag end persists to
  // cardPos, which feeds straight back in here as the new `pos`.
  const dragging = useRef(false);
  useEffect(() => {
    if (dragging.current) return;
    x.set(pos.x);
    y.set(pos.y);
  }, [pos.x, pos.y, x, y]);

  // Grip-only drag (text stays selectable/editable), measured against the canvas zoom.
  const startDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onFront();
    const node = nodeRef.current;
    if (!node) return;
    const ox = x.get(), oy = y.get();
    dragging.current = true;
    setIsDragging(true);
    startCanvasDrag(e, node, (dx, dy) => { x.set(ox + dx); y.set(oy + dy); }, () => {
      onMove({ x: Math.round(x.get()), y: Math.round(y.get()) });
    });
    const release = () => {
      dragging.current = false;
      setIsDragging(false);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
  };

  const inner = (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={cn("rounded-xl p-4 h-full", cardClassName)}
      style={collage ? edgePad : undefined}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          {collage && (
            <button
              onPointerDown={startDrag}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 -ml-1 cursor-grab active:cursor-grabbing text-stone-500 hover:text-stone-700 touch-none"
              title="Drag to move"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          <span className="inline-flex items-center min-w-0">
            {labelNode ?? <h3 className="font-['Kalam'] font-bold text-[14px] leading-none text-stone-700 truncate">{label}</h3>}
          </span>
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {pill}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            className="w-6 h-6 flex items-center justify-center rounded-full text-stone-600 hover:bg-stone-100 transition-colors"
            title={collapsed ? "Expand" : "Tuck away"}
          >
            <ChevronDown className={cn("w-4 h-4 transition-transform", collapsed && "-rotate-90")} />
          </button>
        </div>
      </div>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (!collage) return inner;

  return (
    <motion.div
      ref={nodeRef}
      data-collage-key={dataKey}
      data-anchor-id={anchorId}
      data-anchor-label={anchorLabel}
      data-canvas-item=""
      data-canvas-key={dataKey}
      className="absolute top-0 left-0 rounded-xl"
      style={{
        x, y, width: pos.w, zIndex: isDragging ? 60 : zIndex,
        boxShadow: isDragging ? "0 20px 50px rgba(28,25,23,0.22)" : undefined,
        outline: dropHint ? "2px dashed rgba(139,92,246,0.65)" : undefined,
        outlineOffset: dropHint ? 6 : undefined,
      }}
    >
      {inner}
      {overlay}
      {dropHint && (
        <span
          data-export-hide
          className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-violet-600 text-white text-[10px] font-sans font-semibold px-2.5 py-1 shadow pointer-events-none"
        >
          {dropHint}
        </span>
      )}
    </motion.div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function JournalScreen() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const { data: journal, isLoading } = useGetJournal(params.id!);
  const [completedActions, setCompletedActions] = useState<Record<string, boolean>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [freeWrite, setFreeWrite] = useState("");
  const [showStickerPanel, setShowStickerPanel] = useState(false);
  const [nextStepsSheetOpen, setNextStepsSheetOpen] = useState(false);
  const [nextStepsCollapsed, setNextStepsCollapsed] = useState(false); // web hero collapse → logo only
  const [stickers, setStickers] = useState<PlacedSticker[]>([]);
  const [bgImage, setBgImage] = useState<string | null>(null);
  // Floating toolbar — per-section typography (tap a section to select, then style it)
  type SecStyle = { color?: string | null; font?: FontKey; scale?: number };
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [sectionStyles, setSectionStyles] = useState<Record<string, SecStyle>>({});
  // "See more" modal for reading/editing a section's full text
  // Rich-text colour overlay per field (HTML, persisted locally; backend keeps plain text)
  const [rich, setRich] = useState<Record<string, string>>({});
  // Draggable free-move collage (web) + tuck/collapse (both). Positions & collapsed persisted per entry.
  const isCollage = useMediaQuery("(min-width: 1024px)");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [cardPos, setCardPos] = useState<Record<string, { x: number; y: number }>>({});
  const [frontOrder, setFrontOrder] = useState<string[]>([]);
  const collageRef = useRef<HTMLDivElement>(null);
  // Desktop infinite canvas (viewport API), sticker→card drop hint, and the visible
  // area the sketch layer must cover while drawing.
  const canvasRef = useRef<JournalCanvasApi>(null);
  const stickerLayerRef = useRef<HTMLDivElement>(null);
  const [anchorHover, setAnchorHover] = useState<string | null>(null);
  const [sketchView, setSketchView] = useState<WorldRect | null>(null);
  const [nextStepsPing, setNextStepsPing] = useState(0);
  // Floating toolbar — sketch
  const [sketchActive, setSketchActive] = useState(false);
  const [penColor, setPenColor] = useState("#7c3aed");
  const [penSize, setPenSize] = useState(4);
  const [eraser, setEraser] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const journalContainerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Color theme picker
  const [localTag, setLocalTag] = useState<JournalLibraryTag>("Self-care");
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Download / Export menu
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Share / Social menu
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showShareMenu) return;
    const handler = (e: MouseEvent) => {
      if (!shareMenuRef.current?.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showShareMenu]);

  // Editable content fields
  const [localTitle, setLocalTitle] = useState("");
  const [localMood, setLocalMood] = useState("");
  const [localPullQuote, setLocalPullQuote] = useState("");
  const [localOpeningReflection, setLocalOpeningReflection] = useState("");
  const [localCoreInsight, setLocalCoreInsight] = useState("");
  const [localWhatSurfaced, setLocalWhatSurfaced] = useState("");
  const [localReflectionPrompt, setLocalReflectionPrompt] = useState("");

  // Editable actions
  const [localActions, setLocalActions] = useState<Array<{ id: string; text: string; completed: boolean; subPrompts: string[]; notes: string }>>([]);
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());
  const [actionNotes, setActionNotes] = useState<Record<string, string>>({});
  const actionNoteSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [lastDeletedStep, setLastDeletedStep] = useState<{ text: string; subPrompts: string[]; notes: string; index: number } | null>(null);
  const deletedStepTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Hidden sections (deleted pre-added content)
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());

  // Attribution panel
  const [attributionOpen, setAttributionOpen] = useState(false);
  const [attributionSection, setAttributionSection] = useState<string | null>(null);

  // Share link points at the PUBLIC preview route (/share/journal/:id), not
  // window.location.href — that's the private, authenticated /journal/:id page, which
  // whoever the link is sent to can't open (they'd have bounced straight to the
  // marketing homepage, which is exactly the "shares the homepage" bug this fixes).
  const getPublicShareUrl = useCallback(
    () => `${window.location.origin}/share/journal/${params.id}`,
    [params.id],
  );

  const copyToClipboard = useCallback(async (platform: string) => {
    const text = `${localTitle || "My Unfolding"}\n${getPublicShareUrl()}`;
    await navigator.clipboard.writeText(text);
    setCopiedPlatform(platform);
    setTimeout(() => setCopiedPlatform(null), 1800);
  }, [localTitle, getPublicShareUrl]);

  const queryClient = useQueryClient();
  const deleteJournal = useDeleteJournal({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["/api/journals"] });
        setLocation("/unfoldings");
      },
    },
  });


  // Initialize editable content from journal data
  useEffect(() => {
    if (!journal) return;
    const j = journal as unknown as Record<string, unknown>;
    setLocalTag(coerceJournalTagForDisplay(j.tag as string));
    setLocalTitle((j.title as string) ?? "");
    setLocalMood((j.mood as string) ?? "");
    setLocalPullQuote((j.pullQuote as string) ?? "");
    setLocalOpeningReflection((j.openingReflection as string) ?? "");
    setLocalCoreInsight((j.coreInsight as string) ?? "");
    setLocalWhatSurfaced((j.whatSurfaced as string) ?? "");
    setLocalReflectionPrompt((j.reflectionPrompt as string) ?? "");
    setFreeWrite((j.freeWrite as string) ?? "");
    if (Array.isArray(j.actions)) {
      const typed = j.actions as Array<{ id: string; text: string; completed: boolean; subPrompts?: string[]; notes?: string }>;
      setLocalActions(typed.map((a) => ({
        id: a.id, text: a.text, completed: a.completed,
        subPrompts: a.subPrompts ?? [], notes: a.notes ?? "",
      })));
      const notesMap: Record<string, string> = {};
      typed.forEach((a) => { notesMap[a.id] = a.notes ?? ""; });
      setActionNotes(notesMap);
      const autoExpand = new Set<string>();
      typed.forEach((a) => { if (a.notes && a.notes.trim()) autoExpand.add(a.id); });
      setExpandedActions(autoExpand);
    }
  }, [journal]);

  // Debounced save of journal content fields
  const contentSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const freeWriteSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveFreeWrite = useCallback((text: string) => {
    if (!params.id) return;
    if (freeWriteSaveTimer.current) clearTimeout(freeWriteSaveTimer.current);
    freeWriteSaveTimer.current = setTimeout(() => {
      authFetch(apiUrl(`/journals/${params.id}/free-write`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ freeWrite: text }),
      }).catch(() => {});
    }, 800);
  }, [params.id]);
  // Real request state — never a timer. "saving" is set when the debounced PATCH
  // actually goes out, and only a successful response flips it to "saved".
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const saveContent = useCallback((patch: Record<string, string>) => {
    if (!params.id) return;
    if (contentSaveTimer.current) clearTimeout(contentSaveTimer.current);
    contentSaveTimer.current = setTimeout(() => {
      setSaveState("saving");
      authFetch(apiUrl(`/journals/${params.id}/content`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      })
        .then((res) => setSaveState(res.ok ? "saved" : "error"))
        .catch(() => setSaveState("error"));
    }, 600);
  }, [params.id]);

  // Rich-text colour overlay: HTML per field stored locally; plain text still saved to backend.
  const richKey = params.id ? `unfold:journalRich:${params.id}` : null;
  useEffect(() => {
    if (!richKey) return;
    try { const raw = localStorage.getItem(richKey); setRich(raw ? JSON.parse(raw) : {}); } catch { setRich({}); }
  }, [richKey]);
  const setRichField = useCallback((field: string, html: string, backendKey?: string) => {
    setRich((prev) => {
      const next = { ...prev, [field]: html };
      if (richKey) { try { localStorage.setItem(richKey, JSON.stringify(next)); } catch { /* ignore */ } }
      return next;
    });
    if (backendKey) saveContent({ [backendKey]: stripHtml(html) });
  }, [richKey, saveContent]);
  const richValue = (field: string, fallback: string, isHtml = false) =>
    (rich[field] !== undefined ? rich[field] : (isHtml ? fallback : escapeHtml(fallback)));

  // Editable section heading (text, colour, font, size) — used as CollageCard's labelNode.
  const headingNode = (key: string, defaultLabel: string) => (
    <RichText
      value={richValue(`${key}Label`, defaultLabel)}
      onChange={(h) => setRichField(`${key}Label`, h)}
      onFocus={() => setSelectedSection(`${key}Label`)}
      singleLine
      className="font-['Kalam'] font-bold text-[14px] leading-none text-stone-700"
      style={secStyle(`${key}Label`, 14)}
    />
  );

  // Helpers for hiding sections
  const hideSection = useCallback((id: string) => {
    setHiddenSections((prev) => new Set([...prev, id]));
  }, []);

  // Add a new action item
  const handleAddAction = useCallback(async () => {
    if (!params.id) return;
    const res = await authFetch(apiUrl(`/journals/${params.id}/actions`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: "New step…" }),
    });
    if (res.ok) {
      const { id } = await res.json();
      setLocalActions((prev) => [...prev, { id, text: "New step…", completed: false, subPrompts: [], notes: "" }]);
    }
  }, [params.id]);

  // Update action text
  const handleUpdateActionText = useCallback((id: string, text: string) => {
    setLocalActions((prev) => prev.map((a) => a.id === id ? { ...a, text } : a));
    authFetch(apiUrl(`/journals/${params.id}/actions/${id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    }).catch(() => {});
  }, [params.id]);

  // Delete action — stash it so it can be undone from the toolbar
  const handleDeleteAction = useCallback((id: string) => {
    setLocalActions((prev) => {
      const idx = prev.findIndex((a) => a.id === id);
      const act = prev[idx];
      if (act) {
        setLastDeletedStep({ text: act.text, subPrompts: act.subPrompts, notes: actionNotes[id] ?? act.notes ?? "", index: idx });
        if (deletedStepTimer.current) clearTimeout(deletedStepTimer.current);
        deletedStepTimer.current = setTimeout(() => setLastDeletedStep(null), 8000);
      }
      return prev.filter((a) => a.id !== id);
    });
    authFetch(apiUrl(`/journals/${params.id}/actions/${id}`), {
      method: "DELETE",
    }).catch(() => {});
  }, [params.id, actionNotes]);

  // Undo the last deleted step — recreate it in place
  const handleUndoDeleteStep = useCallback(async () => {
    const del = lastDeletedStep;
    if (!del || !params.id) return;
    setLastDeletedStep(null);
    if (deletedStepTimer.current) clearTimeout(deletedStepTimer.current);
    const res = await authFetch(apiUrl(`/journals/${params.id}/actions`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: del.text }),
    });
    if (res.ok) {
      const { id } = await res.json();
      setLocalActions((prev) => {
        const arr = [...prev];
        arr.splice(Math.min(del.index, arr.length), 0, { id, text: del.text, completed: false, subPrompts: del.subPrompts, notes: del.notes });
        return arr;
      });
    }
  }, [lastDeletedStep, params.id]);

  // Save action notes with debounce
  const handleActionNoteChange = useCallback((id: string, notes: string) => {
    setActionNotes((prev) => ({ ...prev, [id]: notes }));
    if (actionNoteSaveTimers.current[id]) clearTimeout(actionNoteSaveTimers.current[id]);
    actionNoteSaveTimers.current[id] = setTimeout(() => {
      authFetch(apiUrl(`/journals/${params.id}/actions/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      }).catch(() => {});
    }, 800);
  }, [params.id]);

  // Load stickers for this journal (track loaded state for photo-conversion)
  const [stickersLoaded, setStickersLoaded] = useState(false);
  useEffect(() => {
    if (!params.id) return;
    authFetch(apiUrl(`/journals/${params.id}/stickers`))
      .then((r) => r.ok ? r.json() : [])
      .then((data: PlacedSticker[]) => { setStickers(data); setStickersLoaded(true); })
      .catch(() => { setStickersLoaded(true); });
  }, [params.id]);

  // Custom background photo (persisted locally per journal)
  const bgStorageKey = params.id ? `unfold:journalBg:${params.id}` : null;
  useEffect(() => {
    if (!bgStorageKey) return;
    try {
      setBgImage(localStorage.getItem(bgStorageKey) || null);
    } catch { /* storage unavailable */ }
  }, [bgStorageKey]);

  const handleSetBackground = useCallback((dataUrl: string | null) => {
    setBgImage(dataUrl);
    if (!bgStorageKey) return;
    try {
      if (dataUrl) localStorage.setItem(bgStorageKey, dataUrl);
      else localStorage.removeItem(bgStorageKey);
    } catch { /* quota exceeded or unavailable */ }
  }, [bgStorageKey]);

  // Per-section styles + sketch (persisted locally per journal, like the background)
  const sectionStylesKey = params.id ? `unfold:journalSectionStyles:${params.id}` : null;
  const sketchStorageKey = params.id ? `unfold:journalSketch:${params.id}` : null;

  // Desktop canvas arrangement (card/title/Source positions + drawings) persists with the
  // journal on the server; localStorage below remains this browser's cache and fallback.
  const { layoutChanged } = useCanvasLayoutSync({
    journalId: params.id,
    enabled: isCollage,
    positions: cardPos,
    drawings: strokes,
    onServerLayout: (layout) => {
      setCardPos(layout.positions);
      setStrokes(layout.drawings);
      if (!params.id) return;
      try {
        const collageCacheKey = `unfold:journalCollage:${params.id}`;
        const prev = JSON.parse(localStorage.getItem(collageCacheKey) ?? "{}");
        localStorage.setItem(collageCacheKey, JSON.stringify({ ...prev, pos: layout.positions }));
        localStorage.setItem(`unfold:journalSketch:${params.id}`, JSON.stringify(layout.drawings));
      } catch { /* storage unavailable */ }
    },
  });

  useEffect(() => {
    if (!sectionStylesKey) return;
    try {
      const raw = localStorage.getItem(sectionStylesKey);
      setSectionStyles(raw ? JSON.parse(raw) : {});
    } catch { setSectionStyles({}); }
  }, [sectionStylesKey]);

  useEffect(() => {
    if (!sketchStorageKey) return;
    try {
      const raw = localStorage.getItem(sketchStorageKey);
      setStrokes(raw ? JSON.parse(raw) : []);
    } catch { setStrokes([]); }
  }, [sketchStorageKey]);

  // Update the style of the currently selected section and persist the whole map.
  const updateSelectedSection = useCallback((patch: SecStyle) => {
    if (!selectedSection) return;
    setSectionStyles((prev) => {
      const next = { ...prev, [selectedSection]: { ...prev[selectedSection], ...patch } };
      if (sectionStylesKey) { try { localStorage.setItem(sectionStylesKey, JSON.stringify(next)); } catch { /* ignore */ } }
      return next;
    });
  }, [selectedSection, sectionStylesKey]);

  const persistStrokes = useCallback((next: Stroke[]) => {
    setStrokes(next);
    layoutChanged();
    if (!sketchStorageKey) return;
    try { localStorage.setItem(sketchStorageKey, JSON.stringify(next)); } catch { /* ignore */ }
  }, [sketchStorageKey, layoutChanged]);

  const handleCommitStroke = useCallback((s: Stroke) => {
    setStrokes((prev) => {
      const next = [...prev, s];
      if (sketchStorageKey) { try { localStorage.setItem(sketchStorageKey, JSON.stringify(next)); } catch { /* ignore */ } }
      return next;
    });
    layoutChanged();
  }, [sketchStorageKey, layoutChanged]);

  // Compute inline CSS overrides for a section, merged onto its base text style.
  const secStyle = useCallback((key: string, baseSizePx: number): React.CSSProperties => {
    const o = sectionStyles[key];
    if (!o) return {};
    const css: React.CSSProperties = {};
    if (o.color) css.color = o.color;
    if (o.font && o.font !== "default") {
      const f = FONTS.find((x) => x.key === o.font)?.css;
      if (f) css.fontFamily = f;
    }
    if (o.scale && o.scale !== 1) css.fontSize = `${Math.round(baseSizePx * o.scale)}px`;
    return css;
  }, [sectionStyles]);

  // Human labels + current values for the selected section (drives the toolbar).
  const SECTION_LABELS: Record<string, string> = {
    title: "Title", mood: "Mood", dateLine: "Date", unfoldedLine: "Source line",
    openingReflection: "Opening Reflection", pullQuote: "Pull Quote",
    coreInsight: "Core Insight", whatSurfaced: "What Surfaced", reflectionPrompt: "Sit With This",
  };
  const selCur: SecStyle = selectedSection ? (sectionStyles[selectedSection] ?? {}) : {};

  // Tap-to-select: click a section to make it the toolbar's target (no visual outline).
  const selectHandler = (key: string) => (e: React.MouseEvent) => { e.stopPropagation(); setSelectedSection(key); };
  const selOutline = (_key: string): React.CSSProperties => ({});

  // ── Draggable collage layout + tuck/collapse (persisted per entry) ──
  const collageKey = params.id ? `unfold:journalCollage:${params.id}` : null;
  useEffect(() => {
    if (!collageKey) return;
    // First open → all cards tucked (collapsed). After that, restore the user's last state.
    const allTucked = () => new Set(Object.keys(COLLAGE_DEFAULTS));
    try {
      const raw = localStorage.getItem(collageKey);
      if (raw) { const d = JSON.parse(raw); setCardPos(d.pos ?? {}); setCollapsed(new Set(d.collapsed ?? [])); }
      else { setCardPos({}); setCollapsed(isCollage ? new Set<string>() : allTucked()); }
    } catch { setCollapsed(isCollage ? new Set<string>() : allTucked()); }
  }, [collageKey]);
  const persistCollage = useCallback((pos: Record<string, { x: number; y: number }>, coll: Set<string>) => {
    if (!collageKey) return;
    try { localStorage.setItem(collageKey, JSON.stringify({ pos, collapsed: [...coll] })); } catch { /* ignore */ }
  }, [collageKey]);
  const moveCard = useCallback((key: string, p: { x: number; y: number }) => {
    setCardPos((prev) => { const next = { ...prev, [key]: p }; persistCollage(next, collapsed); return next; });
    layoutChanged();
  }, [persistCollage, collapsed, layoutChanged]);
  const bringToFront = useCallback((key: string) => setFrontOrder((prev) => [...prev.filter((k) => k !== key), key]), []);
  const toggleCollapse = useCallback((key: string) => {
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(key)) { n.delete(key); bringToFront(key); } else { n.add(key); }
      persistCollage(cardPos, n);
      return n;
    });
  }, [persistCollage, cardPos, bringToFront]);
  const zIndexFor = (key: string) => { const i = frontOrder.indexOf(key); return i === -1 ? 1 : 10 + i; };
  // Measured, width-aware defaults; falls back to the static safe layout until the
  // canvas has been measured. Saved positions from dragging always win.
  const [autoLayout, setAutoLayout] = useState<Record<string, CardPos> | null>(null);
  const posFor = (key: string): CardPos => ({
    ...(COLLAGE_DEFAULTS[key] ?? { x: 0, y: 0, w: 300 }),
    ...(autoLayout?.[key] ?? {}),
    ...(cardPos[key] ?? {}),
  });

  // Collage canvas fits its cards exactly (no dead space when cards are tucked).
  const [collageHeight, setCollageHeight] = useState(COLLAGE_HEIGHT);
  useEffect(() => {
    if (!isCollage) return;
    const cont = collageRef.current;
    if (!cont) return;
    const recompute = () => {
      // 1) Re-pack the default layout for the width we actually have.
      const width = cont.clientWidth;
      if (width > 0) {
        const heights: Record<string, number> = {};
        cont.querySelectorAll<HTMLElement>("[data-collage-key]").forEach((el) => {
          const k = el.dataset.collageKey;
          if (k) heights[k] = el.offsetHeight;
        });
        const next = computeCollageLayout(width, heights);
        setAutoLayout((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      }

      // 2) Fit the canvas to its cards (no dead space when cards are tucked).
      // Rects are in screen px, which the desktop canvas may be zooming.
      const top = cont.getBoundingClientRect().top;
      const k = unscaleFactor(cont);
      let maxB = 0;
      for (const ch of Array.from(cont.children) as HTMLElement[]) {
        if (getComputedStyle(ch).position === "fixed") continue; // hero Next Steps is out of flow
        maxB = Math.max(maxB, (ch.getBoundingClientRect().bottom - top) * k);
      }
      setCollageHeight(Math.max(140, Math.ceil(maxB) + 16));
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(cont);
    Array.from(cont.children).forEach((c) => ro.observe(c));
    window.addEventListener("resize", recompute);
    const t = setTimeout(recompute, 260);
    return () => { ro.disconnect(); window.removeEventListener("resize", recompute); clearTimeout(t); };
  }, [isCollage, collapsed, cardPos, rich, localOpeningReflection, localCoreInsight, localWhatSurfaced, localReflectionPrompt]);

  // Responsive layout: mobile = full-width vertical stack (like the scrapbook page),
  // web (lg+) = scrapbook grid — the four reflection cards in one row, Next Steps full width.
  const sectionsWrapCls =
    "flex flex-col gap-3.5 " +
    "lg:grid lg:grid-cols-4 lg:gap-4 lg:items-start lg:auto-rows-min";
  const slideCls = "w-full lg:w-auto";

  /** Desktop canvas: a spot near the middle of the current view for a new sticker of ~`size` px. */
  const newStickerSpot = useCallback((size: number): { xPct: string; yPct: string } | null => {
    const view = isCollage ? canvasRef.current?.getViewWorldRect() : null;
    if (!view) return null;
    const jitter = () => (Math.random() - 0.5) * Math.min(240, view.w * 0.3);
    const cx = view.x + view.w / 2 + jitter();
    const cy = view.y + view.h * 0.45 + jitter();
    return {
      xPct: (((cx - size / 2) / LAYOUT_REFERENCE_WIDTH) * 100).toFixed(1),
      yPct: (cy - size / 2).toFixed(0),
    };
  }, [isCollage]);

  // Add sticker from library
  const handleAddSticker = useCallback(async (item: StickerItem) => {
    if (!params.id) return;
    // Desktop canvas: land where the user is looking. Otherwise scatter naturally.
    const spot = newStickerSpot(80);
    const xPct = spot?.xPct ?? (15 + Math.random() * 65).toFixed(1);
    const yPct = spot?.yPct ?? (200 + Math.random() * 600).toFixed(0);
    const rotation = Math.floor(Math.random() * 26) - 13;
    const res = await authFetch(apiUrl(`/journals/${params.id}/stickers`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: item.type,
        content: item.content,
        label: item.label,
        xPct,
        yPct,
        rotation,
        scale: item.defaultScale ?? "1",
      }),
    });
    if (res.ok) {
      const { id } = await res.json();
      setStickers((prev) => [...prev, {
        id, journalId: params.id!, type: item.type as PlacedSticker["type"],
        content: item.content, label: item.label,
        xPct, yPct, rotation, scale: item.defaultScale ?? "1",
      }]);
      setShowStickerPanel(false);
    }
  }, [params.id, newStickerSpot]);

  // Upload custom image as sticker
  const handleUploadSticker = useCallback(async (
    dataUrl: string,
    fileName: string,
    dropPos?: { xPct: string; yPct: string },
  ) => {
    if (!params.id) return;
    const spot = dropPos ?? newStickerSpot(120);
    const xPct = spot?.xPct ?? (20 + Math.random() * 50).toFixed(1);
    const yPct = spot?.yPct ?? (300 + Math.random() * 400).toFixed(0);
    const rotation = Math.floor(Math.random() * 14) - 7;
    const res = await authFetch(apiUrl(`/journals/${params.id}/stickers`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "upload", content: dataUrl, label: fileName,
        xPct, yPct, rotation, scale: "1.2",
      }),
    });
    if (res.ok) {
      const { id } = await res.json();
      setStickers((prev) => [...prev, {
        id, journalId: params.id!, type: "upload",
        content: dataUrl, label: fileName,
        xPct, yPct, rotation, scale: "1.2",
      }]);
      setShowStickerPanel(false);
    }
  }, [params.id, newStickerSpot]);

  // Canvas drag-and-drop handler
  const handleCanvasDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = Array.from(e.dataTransfer.files).find(
      (f) => f.type === "image/png" || f.type === "image/jpeg"
    );
    if (!file) return;

    // Compute drop position relative to the container
    const container = journalContainerRef.current;
    const rect = container?.getBoundingClientRect();
    let xPct: string, yPct: string;
    const world = isCollage ? canvasRef.current?.clientToWorld(e.clientX, e.clientY) : null;
    if (world) {
      // Desktop canvas: world px (x as % of the layout reference width).
      xPct = ((world.x / LAYOUT_REFERENCE_WIDTH) * 100).toFixed(1);
      yPct = world.y.toFixed(0);
    } else {
      xPct = rect ? (((e.clientX - rect.left) / rect.width) * 100).toFixed(1) : "40";
      yPct = rect ? (e.clientY - rect.top).toFixed(0) : "400";
    }

    // Resize and convert to data URL (same as Upload tab)
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const MAX = 400;
      const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      URL.revokeObjectURL(url);
      handleUploadSticker(dataUrl, file.name, { xPct, yPct });
    };
    img.src = url;
  }, [handleUploadSticker, isCollage]);

  // Move sticker (debounced save)
  const handleMoveSticker = useCallback((id: string, xPct: string, yPct: string) => {
    setStickers((prev) => prev.map((s) => s.id === id ? { ...s, xPct, yPct } : s));
    clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => {
      authFetch(apiUrl(`/journals/${params.id}/stickers/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ xPct, yPct }),
      }).catch(() => {});
    }, 500);
  }, [params.id]);

  // Rotate sticker
  const handleRotateSticker = useCallback((id: string, rotation: number) => {
    setStickers((prev) => prev.map((s) => s.id === id ? { ...s, rotation } : s));
    authFetch(apiUrl(`/journals/${params.id}/stickers/${id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rotation }),
    }).catch(() => {});
  }, [params.id]);

  // Delete sticker
  const handleDeleteSticker = useCallback((id: string) => {
    setStickers((prev) => prev.filter((s) => s.id !== id));
    authFetch(apiUrl(`/journals/${params.id}/stickers/${id}`), {
      method: "DELETE",
    }).catch(() => {});
  }, [params.id]);

  // Update sticky text
  const handleUpdateStickerText = useCallback((id: string, text: string) => {
    setStickers((prev) => prev.map((s) => s.id === id ? { ...s, label: text } : s));
    authFetch(apiUrl(`/journals/${params.id}/stickers/${id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label: text }),
    }).catch(() => {});
  }, [params.id]);

  // Resize sticker
  const handleResizeSticker = useCallback((id: string, scale: string) => {
    setStickers((prev) => prev.map((s) => s.id === id ? { ...s, scale } : s));
    authFetch(apiUrl(`/journals/${params.id}/stickers/${id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scale }),
    }).catch(() => {});
  }, [params.id]);

  // Compress a File to a data URL (max 800px wide, JPEG 0.85)
  const compressFile = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 800;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = reject;
        img.src = ev.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Replace a photo from within the sticker overlay (called by photo sticker's replace button)
  const handleReplacePhotoSticker = useCallback(async (stickerId: string, slot: number, file: File) => {
    if (!params.id) return;
    const dataUrl = await compressFile(file);
    setStickers((prev) => prev.map((s) => s.id === stickerId ? { ...s, content: dataUrl } : s));
    authFetch(apiUrl(`/journals/${params.id}/stickers/${stickerId}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: dataUrl }),
    }).catch(() => {});
  }, [params.id, compressFile]);


  // ── Sticker ↔ block attachment ──
  /**
   * Blocks in this journal that accept attached stickers, by stable id. Today these are
   * the generated sections (their ids are the section keys already used for layout,
   * attribution and styles); user-created blocks would join this list with their own
   * ids. Stickers pointing at an id that isn't here fall back to being free stickers.
   */
  const anchorBlockIds = new Set<string>(COLLAGE_CARD_KEYS);
  const anchorOf = (st: PlacedSticker) => (st.anchorKey && anchorBlockIds.has(st.anchorKey) ? st.anchorKey : null);

  /**
   * Attachment target (a rendered card with `data-anchor-id`) under a screen point. The
   * sticker's current block gets a margin, so dragging it along its own edge doesn't detach it.
   */
  const cardAtPoint = useCallback((px: number, py: number, current?: string | null): string | null => {
    const root = journalContainerRef.current;
    if (!root) return null;
    let hit: string | null = null;
    root.querySelectorAll<HTMLElement>("[data-anchor-id]").forEach((el) => {
      const key = el.dataset.anchorId;
      if (!key) return;
      const r = el.getBoundingClientRect();
      const m = key === current ? 40 : 0;
      if (px >= r.left - m && px <= r.right + m && py >= r.top - m && py <= r.bottom + m) {
        if (!hit || key === current) hit = key;
      }
    });
    return hit;
  }, []);

  /** Immediate sticker PATCH (placement changes), cancelling any pending debounced move. */
  const patchSticker = useCallback((id: string, fields: Partial<Pick<PlacedSticker, "xPct" | "yPct" | "anchorKey">>) => {
    clearTimeout(saveTimers.current[id]);
    setStickers((prev) => prev.map((s) => (s.id === id ? { ...s, ...fields } : s)));
    authFetch(apiUrl(`/journals/${params.id}/stickers/${id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    }).catch(() => {});
  }, [params.id]);

  /**
   * Decides where a dragged sticker lands: dropped on a card (pointer inside it) →
   * attached to that card's edge; an attached sticker dropped elsewhere → free on the
   * canvas. Alt/Option keeps it free. Returns false to let a free→free move save normally.
   */
  const handleStickerDrop = useCallback((id: string, info: StickerDropInfo): boolean => {
    setAnchorHover(null);
    const st = stickers.find((s) => s.id === id);
    if (!st) return false;
    const current = anchorOf(st);
    const target = isAnchorableSticker(st.type) && !info.altKey
      ? cardAtPoint(info.pointerX, info.pointerY, current)
      : null;
    if (target) {
      const el = journalContainerRef.current?.querySelector<HTMLElement>(`[data-anchor-id="${CSS.escape(target)}"]`);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      const k = unscaleFactor(el);
      const spot = projectToCardEdge((info.centerX - r.left) * k, (info.centerY - r.top) * k, el.offsetWidth, el.offsetHeight);
      patchSticker(id, { anchorKey: target, ...spot });
      return true;
    }
    if (current) {
      // Detach: back onto the canvas where it was dropped (world px from the origin).
      const w = canvasRef.current?.clientToWorld(info.centerX, info.centerY);
      if (!w) return false;
      const left = w.x - info.sizePx / 2;
      const top = w.y - info.sizePx / 2;
      patchSticker(id, { anchorKey: null, xPct: ((left / LAYOUT_REFERENCE_WIDTH) * 100).toFixed(2), yPct: top.toFixed(0) });
      return true;
    }
    return false;
  }, [stickers, cardAtPoint, patchSticker]);

  const handleStickerDragMove = useCallback((id: string, point: { x: number; y: number; altKey: boolean } | null) => {
    const st = point ? stickers.find((s) => s.id === id) : null;
    if (!point || !st || !isAnchorableSticker(st.type) || point.altKey) { setAnchorHover(null); return; }
    const target = cardAtPoint(point.x, point.y, anchorOf(st));
    setAnchorHover(target && target !== anchorOf(st) ? target : null);
  }, [stickers, cardAtPoint]);

  // ── Desktop canvas: sketch surface, focus targets, framing insets ──
  /** World-space box around all sketch strokes (for fit-to-content, export and the sketch surface). */
  const strokesBounds = useMemo((): WorldRect | null => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const st of strokes) {
      const r = st.size / 2 + 2;
      for (const pt of st.points) {
        minX = Math.min(minX, pt.x - r); minY = Math.min(minY, pt.y - r);
        maxX = Math.max(maxX, pt.x + r); maxY = Math.max(maxY, pt.y + r);
      }
    }
    return minX === Infinity ? null : { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }, [strokes]);

  // While drawing, the surface must cover wherever the user has panned to.
  useEffect(() => {
    if (!isCollage || !sketchActive) { setSketchView(null); return; }
    setSketchView(canvasRef.current?.getViewWorldRect() ?? null);
  }, [isCollage, sketchActive]);

  const sketchExtend = useMemo((): SketchRect | null =>
    isCollage ? unionRects([strokesBounds, sketchActive ? sketchView : null]) : null,
  [isCollage, strokesBounds, sketchActive, sketchView]);

  const getStrokesBounds = useCallback(() => strokesBounds, [strokesBounds]);

  /**
   * Focus-control order: Source → the four reflection cards → Next Steps → the user's own
   * images and sticky notes (in creation order). Missing items are skipped by the control.
   */
  const hasSource = !!(journal?.sourceUrl || journal?.sourceTitle);
  const getFocusTargets = useCallback((): FocusTarget[] => {
    const t: FocusTarget[] = [];
    if (hasSource) t.push({ key: "sourceCard", label: "Source" });
    for (const k of COLLAGE_CARD_KEYS) t.push({ key: k, label: SECTION_TITLES[k] });
    // Next Steps is the fixed panel, not a canvas object — bring it forward instead of panning.
    t.push({ key: "nextSteps", label: "Next Steps", run: () => { setNextStepsCollapsed(false); setNextStepsPing((n) => n + 1); } });
    for (const st of stickers) {
      if (anchorOf(st) || isAnchorableSticker(st.type)) continue;
      t.push({ key: `sticker:${st.id}`, label: st.type === "sticky" ? "Sticky note" : "Image" });
    }
    return t;
  }, [hasSource, stickers]);

  // Keep framed content clear of the toolbar (bottom) and the open Next Steps panel (right).
  const getCanvasInsets = useCallback(
    () => ({ top: 24, left: 32, bottom: 130, right: nextStepsCollapsed ? 32 : 400 }),
    [nextStepsCollapsed],
  );

  // Close export menu on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  const handleExport = useCallback(async (format: "png" | "jpeg" | "pdf" | "svg") => {
    const node = journalContainerRef.current;
    if (!node) return;
    setExporting(true);
    setShowExportMenu(false);

    const brandingEl = node.querySelector("[data-export-branding]") as HTMLElement | null;
    const hiddenEls = node.querySelectorAll<HTMLElement>("[data-export-hide]");

    // Desktop canvas: capture the content wherever it has been arranged (it can sit at
    // negative coordinates or far outside the world box) rather than the world box, and
    // independent of the current pan/zoom — the node itself is never transformed; the
    // canvas transform lives on its parent, which html-to-image doesn't clone.
    const EXPORT_PAD = 32;
    const bounds = isCollage ? canvasRef.current?.getContentBounds() ?? null : null;
    const brandingPlacement = ["position", "left", "top", "width", "zIndex"] as const;

    function showExportState() {
      if (brandingEl) {
        brandingEl.style.display = "flex";
        if (bounds) {
          Object.assign(brandingEl.style, {
            position: "absolute", left: `${bounds.x}px`, top: `${bounds.y}px`, width: `${bounds.w}px`, zIndex: "40",
          });
          brandingEl.style.top = `${bounds.y - brandingEl.offsetHeight}px`;
        }
      }
      hiddenEls.forEach((el) => { el.style.display = "none"; });
    }
    function restoreState() {
      if (brandingEl) {
        brandingEl.style.display = "none";
        brandingPlacement.forEach((k) => { brandingEl.style[k] = ""; });
      }
      hiddenEls.forEach((el) => { el.style.display = ""; });
    }

    try {
      const fileName = `journal-${localTitle?.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase() || "entry"}`;
      showExportState();

      const brandH = bounds && brandingEl ? brandingEl.offsetHeight : 0;
      const opts = {
        cacheBusts: true,
        backgroundColor: "#f7f7f5",
        ...(bounds && {
          width: Math.ceil(bounds.w + EXPORT_PAD * 2),
          height: Math.ceil(bounds.h + brandH + EXPORT_PAD * 2),
          // Shift the origin so the content bounds land in frame. The root is the
          // zero-size canvas origin; html-to-image sizes it to the frame, which doesn't
          // affect its absolutely positioned content.
          style: {
            position: "relative",
            transform: `translate(${EXPORT_PAD - bounds.x}px, ${EXPORT_PAD + brandH - bounds.y}px)`,
            transformOrigin: "0 0",
          },
        }),
      };

      if (format === "png") {
        const dataUrl = await toPng(node, { ...opts, pixelRatio: 2 });
        downloadDataUrl(dataUrl, `${fileName}.png`);
      } else if (format === "jpeg") {
        const dataUrl = await toJpeg(node, { ...opts, quality: 0.92, pixelRatio: 2 });
        downloadDataUrl(dataUrl, `${fileName}.jpg`);
      } else if (format === "svg") {
        const dataUrl = await toSvg(node, opts);
        downloadDataUrl(dataUrl, `${fileName}.svg`);
      } else if (format === "pdf") {
        const dataUrl = await toPng(node, { ...opts, pixelRatio: 2 });
        const img = new Image();
        img.src = dataUrl;
        await new Promise<void>((resolve) => { img.onload = () => resolve(); });
        const pxWidth = img.width;
        const pxHeight = img.height;
        const pdfWidth = 210; // A4 mm
        const pdfHeight = (pxHeight / pxWidth) * pdfWidth;
        const pdf = new jsPDF({ orientation: pdfHeight > pdfWidth ? "portrait" : "landscape", unit: "mm", format: [pdfWidth, pdfHeight] });
        pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`${fileName}.pdf`);
      }

      restoreState();
    } catch (err) {
      console.error("Export failed:", err);
      restoreState();
    } finally {
      setExporting(false);
    }
  }, [localTitle, isCollage]);

  if (isLoading || !journal) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={PAPER_STYLE}>
        <div className="w-10 h-10 rounded-full border-2 border-stone-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  const palette = getpalette(localTag);
  const dateStr = format(parseISO(journal.createdAt), "MMMM d, yyyy");
  const dayStr = format(parseISO(journal.createdAt), "EEEE").toUpperCase();

  const j = journal as unknown as Record<string, unknown>;
  const journalAttribution = (j.attribution as Record<string, number[]> | null | undefined) ?? null;
  const journalSourceQA = (j.sourceQA as Array<{ question: string; answer: string }> | null | undefined) ?? null;

  function getContributingQA(sectionKey: string): Array<{ question: string; answer: string }> {
    if (!journalAttribution || !journalSourceQA) return [];
    const indices = journalAttribution[sectionKey] ?? [];
    return indices
      .filter((i) => i >= 0 && i < journalSourceQA.length)
      .map((i) => journalSourceQA[i]);
  }

  function SourcePill({ sectionKey, label }: { sectionKey: string; label: string }) {
    const qa = getContributingQA(sectionKey);
    if (qa.length === 0) return null;
    return (
      <button
        data-export-hide
        onClick={() => { setAttributionSection(sectionKey); setAttributionOpen(true); }}
        className="inline-flex items-center gap-1 text-[8px] font-sans text-stone-600 hover:text-stone-800 transition-colors ml-2 align-middle"
        title={`See what shaped ${label}`}
      >
        <span className="text-stone-500">✦</span>
        {qa.length} {qa.length === 1 ? "source" : "sources"}
      </button>
    );
  }

  // Stickers attached to a card vs. free ones. An unknown anchor key is treated as free.
  const freeStickers = stickers.filter((st) => !anchorOf(st));
  const anchoredTo = (key: string) => stickers.filter((st) => anchorOf(st) === key);

  // Mobile: attached stickers sit in normal flow at the bottom of their card — a small
  // decorative strip that can never cover the text or be dragged around.
  const mobileAnchoredStrip = (key: string) => {
    const list = anchoredTo(key);
    if (list.length === 0) return null;
    return (
      <div aria-hidden className="mt-4 shrink-0 flex flex-wrap items-center justify-center gap-3">
        {list.map((st) => <StaticSticker key={st.id} sticker={st} size={44} />)}
      </div>
    );
  };

  // Desktop: attached stickers ride on their card's edge (not resizable while attached —
  // their size is capped to what the padded edge can hold).
  const anchoredStickersFor = (key: string) => (
    <AnchoredStickers
      stickers={anchoredTo(key)}
      onMove={handleMoveSticker}
      onDelete={handleDeleteSticker}
      onRotate={handleRotateSticker}
      onUpdateText={handleUpdateStickerText}
      onDropAt={handleStickerDrop}
      onDragMove={handleStickerDragMove}
    />
  );

  const edgePadFor = (key: string): React.CSSProperties | undefined => {
    const list = anchoredTo(key);
    if (list.length === 0) return undefined;
    const sides = anchoredEdgeSides(list);
    return {
      ...(sides.top && { paddingTop: ANCHOR_EDGE_PAD }),
      ...(sides.right && { paddingRight: ANCHOR_EDGE_PAD }),
      ...(sides.bottom && { paddingBottom: ANCHOR_EDGE_PAD }),
      ...(sides.left && { paddingLeft: ANCHOR_EDGE_PAD }),
    };
  };

  // Mobile: header + 4 section cards as a swipeable vertical stack.
  const mkBodyNode = (key: string, label: string, plain: string, setPlain: (v: string) => void, style: React.CSSProperties) => (
    <div className="p-7 h-full flex flex-col">
      <h3 className="font-['Kalam'] font-bold text-[18px] text-stone-700 mb-4 shrink-0">{label}</h3>
      <RichText
        value={richValue(key, plain)}
        onChange={(h) => { setRichField(key, h, key); setPlain(stripHtml(h)); }}
        onFocus={() => setSelectedSection(key)}
        placeholder={`${label}…`}
        className="flex-1"
        style={style}
      />
      {mobileAnchoredStrip(key)}
    </div>
  );
  // Mobile: free decorative stickers become contained cards instead of floating.
  const mobileDecoStickers = freeStickers.filter((st) => st.type === "svg" || st.type === "emoji");
  const mobileStackItems: StackItem[] = isCollage ? [] : [
    {
      id: "header",
      node: (
        <div className="p-7 h-full flex flex-col justify-center text-center">
          <RichText value={richValue("dateLine", `${dayStr} · ${dateStr}`)} onChange={(h) => setRichField("dateLine", h)} onFocus={() => setSelectedSection("dateLine")} singleLine className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 font-sans mb-3" style={secStyle("dateLine", 10)} />
          <h1 className="text-[2rem] leading-tight text-stone-800">
            <RichText value={richValue("title", localTitle)} onChange={(h) => { setRichField("title", h, "title"); setLocalTitle(stripHtml(h)); }} onFocus={() => setSelectedSection("title")} singleLine placeholder="Journal title" style={{ fontFamily: "'Newsreader', serif", fontStyle: "italic", ...secStyle("title", 34) }} />
          </h1>
          <div className="mt-3 text-[15px] font-['Kalam'] font-bold flex items-baseline gap-1 justify-center" style={{ color: CORAL }}>
            <RichText value={richValue("moodDash", "—")} onChange={(h) => setRichField("moodDash", h)} onFocus={() => setSelectedSection("moodDash")} singleLine style={{ fontFamily: "'Kalam', cursive", fontWeight: 700, color: CORAL, ...secStyle("moodDash", 15) }} />
            <RichText value={richValue("mood", localMood)} onChange={(h) => { setRichField("mood", h, "mood"); setLocalMood(stripHtml(h)); }} onFocus={() => setSelectedSection("mood")} singleLine placeholder="mood" style={{ fontFamily: "'Kalam', cursive", fontWeight: 700, color: CORAL, ...secStyle("mood", 15) }} />
          </div>
        </div>
      ),
    },
    { id: "openingReflection", node: mkBodyNode("openingReflection", "Opening Reflection", localOpeningReflection, setLocalOpeningReflection, { fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "16px", lineHeight: "28px", color: "#1c1917", ...secStyle("openingReflection", 16) }) },
    { id: "coreInsight", node: mkBodyNode("coreInsight", "Core Insight", localCoreInsight, setLocalCoreInsight, { fontFamily: "'Newsreader', serif", fontStyle: "italic", fontSize: "17px", lineHeight: "1.75", color: "#1c1917", ...secStyle("coreInsight", 17) }) },
    { id: "whatSurfaced", node: mkBodyNode("whatSurfaced", "What Surfaced", localWhatSurfaced, setLocalWhatSurfaced, { fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "16px", lineHeight: "28px", color: "#1c1917", ...secStyle("whatSurfaced", 16) }) },
    {
      id: "reflectionPrompt",
      node: (
        <div className="p-7 h-full flex flex-col">
          <h3 className="font-['Kalam'] font-bold text-[18px] text-stone-700 mb-4 shrink-0">Sit With This</h3>
          <RichText value={richValue("reflectionPrompt", localReflectionPrompt)} onChange={(h) => { setRichField("reflectionPrompt", h, "reflectionPrompt"); setLocalReflectionPrompt(stripHtml(h)); }} onFocus={() => setSelectedSection("reflectionPrompt")} placeholder="Your reflection prompt…" style={{ fontFamily: "'Newsreader', serif", fontSize: "17px", fontStyle: "italic", lineHeight: "1.65", color: "#1c1917", ...secStyle("reflectionPrompt", 17) }} />
          <div className="mt-4 border-t border-[#e4ddd4] pt-3 flex-1">
            <p className="text-[9px] text-stone-600 font-sans mb-1">Your thoughts ↓</p>
            <textarea value={freeWrite} onChange={(e) => { setFreeWrite(e.target.value); saveFreeWrite(e.target.value); }} placeholder="Write your reflection here…" rows={4} className="w-full bg-transparent text-stone-900 placeholder:text-stone-500 focus:outline-none resize-none leading-[26px]" style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "16px" }} />
          </div>
          {mobileAnchoredStrip("reflectionPrompt")}
        </div>
      ),
    },
    // User-added images / GIFs become their own cards in the stack
    ...(isCollage ? [] : freeStickers.filter((st) => st.type === "upload" || st.type === "photo" || st.type === "gif").map((st) => ({
      id: `sticker-${st.id}`,
      node: (
        <div className="p-6 h-full flex flex-col items-center justify-center relative">
          <button
            onClick={() => handleDeleteSticker(st.id)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-stone-100 hover:bg-red-50 hover:text-red-500 text-stone-500 flex items-center justify-center transition-colors"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {st.type === "emoji" ? (
            <span style={{ fontSize: 110, lineHeight: 1 }}>{st.content}</span>
          ) : (st.type === "gif" || st.type === "upload" || st.type === "photo") ? (
            <img src={st.content} alt={st.label ?? "media"} className="max-w-full max-h-[70%] object-contain rounded-2xl shadow-md" />
          ) : (
            <div className="w-48 h-48 [&>svg]:w-full [&>svg]:h-full flex items-center justify-center" dangerouslySetInnerHTML={{ __html: st.content }} />
          )}
          {st.label && <p className="mt-4 text-[11px] text-stone-500 font-sans text-center max-w-[80%] truncate">{st.label}</p>}
        </div>
      ),
    }))),
    // Sticky notes hold the user's own writing, so each keeps its own card (editable).
    ...freeStickers.filter((st) => st.type === "sticky").map((st) => ({
      id: `sticky-${st.id}`,
      node: (
        <div className="p-6 h-full flex flex-col items-center justify-center relative">
          <button
            onClick={() => handleDeleteSticker(st.id)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-stone-100 hover:bg-red-50 hover:text-red-500 text-stone-500 flex items-center justify-center transition-colors"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <div className="relative" style={{ width: 240, height: 240 }}>
            <div className="absolute inset-0 [&>svg]:w-full [&>svg]:h-full pointer-events-none" dangerouslySetInnerHTML={{ __html: st.content }} />
            <textarea
              defaultValue={st.label ?? ""}
              onBlur={(e) => { if (e.target.value !== (st.label ?? "")) handleUpdateStickerText(st.id, e.target.value); }}
              placeholder="Write something…"
              aria-label="Sticky note"
              className="absolute bg-transparent border-none outline-none resize-none placeholder:opacity-40"
              style={{
                top: 48, left: 34, width: 182, height: 168, padding: 0,
                fontFamily: "'Kalam', cursive", fontSize: 18, lineHeight: 1.55, color: getStickyTextColor(st.content),
              }}
            />
          </div>
        </div>
      ),
    })),
    // Each free decorative sticker gets its own small contained card (never floating).
    ...mobileDecoStickers.map((st) => ({
      id: `sticker-card-${st.id}`,
      node: (
        <div className="p-6 h-full flex flex-col items-center justify-center relative">
          <button
            onClick={() => handleDeleteSticker(st.id)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-stone-100 hover:bg-red-50 hover:text-red-500 text-stone-500 flex items-center justify-center transition-colors"
            title="Remove"
            aria-label="Remove sticker"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <StaticSticker sticker={st} size={140} />
        </div>
      ),
    })),
  ];

  return (
    <div
      ref={pageRef}
      style={PAPER_STYLE}
      className="min-h-screen lg:h-screen lg:overflow-hidden lg:flex lg:flex-col pb-4 lg:pb-0 relative overflow-hidden"
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setIsDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); }}
      onDrop={handleCanvasDrop}
    >
      {/* Custom background photo — fills the whole page so there's no bare paper below content */}
      {bgImage && (
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
      )}

      {/* Floating colour bar for text selections (rich-text) */}
      <SelectionColorBar />

      {/* Drop overlay */}
      {isDragOver && (
        <div className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center">
          <div className="absolute inset-4 rounded-2xl border-2 border-dashed border-stone-400 bg-white/60 backdrop-blur-sm" />
          <div className="relative z-10 flex flex-col items-center gap-3 text-stone-500">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect x="8" y="12" width="32" height="28" rx="3" stroke="currentColor" strokeWidth="2" fill="none"/>
              <path d="M24 8v16M17 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p className="text-sm font-sans font-semibold tracking-wide">Drop image to place on canvas</p>
            <p className="text-xs font-sans text-stone-400">PNG or JPEG</p>
          </div>
        </div>
      )}
      {/* Top nav bar */}
      <div className="sticky top-0 z-20 bg-[#f7f7f5]/80 backdrop-blur-sm border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setLocation("/unfoldings")}
          className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 transition-colors font-sans">
          <ArrowLeft className="w-4 h-4" /> Your Unfoldings
        </button>
        <div className="flex items-center gap-1">
          {/* Download / Export */}
          <div ref={exportMenuRef} className="relative">
            <button
              onClick={() => setShowExportMenu((p) => !p)}
              disabled={exporting}
              className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-all disabled:opacity-50"
              title="Export journal"
            >
              {exporting
                ? <div className="w-4 h-4 rounded-full border-2 border-stone-300 border-t-transparent animate-spin" />
                : <Download className="w-4 h-4" />}
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-10 z-40 bg-white border border-[#e4ddd4] shadow-lg rounded-lg py-1 min-w-[140px] font-sans">
                {(["png", "jpeg", "pdf", "svg"] as const).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => handleExport(fmt)}
                    className="w-full text-left px-4 py-2 text-sm text-stone-600 hover:bg-stone-50 hover:text-stone-800 transition-colors"
                  >
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Share */}
          <div ref={shareMenuRef} className="relative">
            <button
              onClick={() => setShowShareMenu((p) => !p)}
              className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-all"
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {showShareMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-10 z-50 w-48 bg-white border border-[#e4ddd4] shadow-xl rounded-xl overflow-hidden"
                >
                  <button
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 transition-colors font-sans"
                    onClick={() => copyToClipboard("link")}
                  >
                    <Copy className="w-4 h-4 text-stone-400" />
                    <span>{copiedPlatform === "link" ? "Copied!" : "Copy to clipboard"}</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Delete */}
          <button onClick={() => setShowDeleteConfirm(true)}
            className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-red-400 hover:bg-red-50 transition-all">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Journal canvas — full width so stickers can go anywhere. On desktop this is the
          "world" of the infinite canvas: a fixed-width box the viewport pans and zooms. */}
      {(() => {
      const world = (
      <div
        ref={journalContainerRef}
        className="journal-paper relative w-full"
        // Desktop: the zero-size canvas origin — everything is positioned from here and may
        // extend in any direction; there is no world box to clip or bound it.
        style={isCollage ? { position: "absolute", left: 0, top: 0, width: 0, height: 0 } : undefined}
      >
        {/* Branding header — hidden on screen, shown during export */}
        <div data-export-branding className="items-center justify-between px-8 py-5 border-b border-[#e4ddd4]" style={{ display: "none", background: "#f7f7f5" }}>
          <img src="/images/UnfoldWin -logo.png" alt="UnfoldWin" className="h-8 w-auto" />
          <span className="text-[12px] text-stone-400 font-sans tracking-wide">unfoldwin.com</span>
        </div>

        {/* Free stickers — desktop canvas only. Mobile has no floating overlay at all:
            there stickers are contained in the card stack (see mobileStackItems).
            Stickers attached to a card render inside that card (AnchoredStickers). */}
        {isCollage && (
          <StickerOverlay
            stickers={freeStickers}
            containerRef={stickerLayerRef}
            referenceWidth={LAYOUT_REFERENCE_WIDTH}
            onMove={handleMoveSticker}
            onDelete={handleDeleteSticker}
            onRotate={handleRotateSticker}
            onResize={handleResizeSticker}
            onUpdateText={handleUpdateStickerText}
            onReplacePhoto={handleReplacePhotoSticker}
            onDropAt={handleStickerDrop}
            onDragMove={handleStickerDragMove}
          />
        )}

        {/* Freehand sketch layer (Apple Preview–style markup) */}
        <SketchCanvas
          active={sketchActive}
          color={penColor}
          size={penSize}
          eraser={eraser}
          strokes={strokes}
          onCommitStroke={handleCommitStroke}
          containerRef={journalContainerRef}
          extend={sketchExtend}
        />

        {/* Source — desktop: a movable canvas object at the top-left of the world
            (previously pinned to the window). Mobile keeps its inline link below. */}
        {isCollage && (journal.sourceUrl || journal.sourceTitle) && (
          <div className="absolute left-6 top-3 z-30 w-[220px] pointer-events-none">
            <CanvasDrag handle="body" pos={posFor("sourceCard")} onMove={(p) => moveCard("sourceCard", p)}>
              <div data-canvas-key="sourceCard" className="origin-top-left scale-50 pointer-events-auto">
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-stone-500 font-sans mb-1 text-left">Source</p>
                <SourceWidget
                  sourceUrl={journal.sourceUrl ?? undefined}
                  sourceTitle={journal.sourceTitle}
                  sourceType={journal.sourceType ?? undefined}
                  accentColor={palette.accent}
                />
              </div>
            </CanvasDrag>
          </div>
        )}

      {/* ─────────────────────────────────────────────────────── */}
      {/* Journal layout — structured planner-journal hybrid      */}
      {/* ─────────────────────────────────────────────────────── */}
      <div
        onClick={() => setSelectedSection(null)}
        className="relative z-10 max-w-2xl lg:max-w-7xl mx-auto px-5 pt-4 pb-6"
        // Desktop canvas: the default layout's content column, placed at fixed world
        // coordinates (as the page column sat in a 1440px window) — never window-relative.
        style={isCollage ? {
          position: "absolute", top: 0, left: (LAYOUT_REFERENCE_WIDTH - LAYOUT_CONTENT_WIDTH) / 2,
          width: LAYOUT_CONTENT_WIDTH, maxWidth: "none", margin: 0,
        } : undefined}
      >

        {/* ══ HEADER — title · source, scrapbook style ══ */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-3">
          <div className="relative flex flex-col">
            {/* Title card — web only; draggable on the desktop canvas by its hover grip. */}
            {isCollage && (
            <CanvasDrag handle="grip" pos={posFor("header")} onMove={(p) => moveCard("header", p)} className="mx-auto w-full max-w-2xl">
            <div
              data-canvas-key="header"
              className="mx-auto w-full max-w-2xl text-center rounded-[28px] px-6 py-4"
              style={{
                background: "rgba(255,255,255,0.5)",
                backdropFilter: "blur(20px) saturate(180%)",
                WebkitBackdropFilter: "blur(20px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.6)",
                boxShadow: "0 8px 30px rgba(28,25,23,0.12), inset 0 1px 0 rgba(255,255,255,0.7)",
              }}
            >
              <RichText
                value={richValue("dateLine", `${dayStr} · ${dateStr}`)}
                onChange={(h) => setRichField("dateLine", h)}
                onFocus={() => setSelectedSection("dateLine")}
                singleLine
                className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 font-sans mb-2 mt-0 lg:text-center"
                style={secStyle("dateLine", 10)}
              />
              <h1 className="text-[1.7rem] lg:text-[2.1rem] leading-tight text-stone-800">
                <RichText
                  value={richValue("title", localTitle)}
                  onChange={(h) => { setRichField("title", h, "title"); setLocalTitle(stripHtml(h)); }}
                  onFocus={() => setSelectedSection("title")}
                  singleLine
                  placeholder="Journal title"
                  style={{ fontFamily: "'Newsreader', serif", fontStyle: "italic", ...secStyle("title", 34) }}
                />
              </h1>
              <div className="mt-1.5 text-[15px] font-['Kalam'] font-bold flex items-baseline gap-1 justify-center" style={{ color: CORAL }}>
                <RichText
                  value={richValue("moodDash", "—")}
                  onChange={(h) => setRichField("moodDash", h)}
                  onFocus={() => setSelectedSection("moodDash")}
                  singleLine
                  style={{ fontFamily: "'Kalam', cursive", fontWeight: 700, color: CORAL, ...secStyle("moodDash", 15) }}
                />
                <RichText
                  value={richValue("mood", localMood)}
                  onChange={(h) => { setRichField("mood", h, "mood"); setLocalMood(stripHtml(h)); }}
                  onFocus={() => setSelectedSection("mood")}
                  singleLine
                  placeholder="mood or intention"
                  className="text-[15px]"
                  style={{ fontFamily: "'Kalam', cursive", fontWeight: 700, color: CORAL, ...secStyle("mood", 15) }}
                />
              </div>
              {journal.sourceTitle && (
                <div className="mt-1 text-[11px] text-stone-500 font-sans">
                  <RichText
                    value={richValue("unfoldedLine", `Unfolded from: <span style="font-style:italic">${escapeHtml(cleanSocialSourceTitle(journal.sourceTitle))}</span>`, true)}
                    onChange={(h) => setRichField("unfoldedLine", h)}
                    onFocus={() => setSelectedSection("unfoldedLine")}
                    style={secStyle("unfoldedLine", 11)}
                  />
                </div>
              )}
            </div>
            </CanvasDrag>
            )}
            {!isCollage && (
              <div className="flex flex-col-reverse items-start gap-2 w-full mb-3">
                {/* Mobile: minimal "▶ Source" link */}
                {(journal.sourceUrl || journal.sourceTitle) && (
                <a
                  href={journal.sourceUrl ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary hover:opacity-80 transition-opacity"
                  title={journal.sourceTitle ? cleanSocialSourceTitle(journal.sourceTitle) : "Source"}
                >
                  <Play className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] font-sans">Source</span>
                </a>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* ══ MOBILE — swipeable vertical card stack (header + 4 sections) ══ */}
        {!isCollage && (
          <VerticalCardStack items={mobileStackItems} cardWidth={320} cardHeight={380} className="min-h-[62vh] py-6 -mt-[11px]" />
        )}

        {/* ══ SECTION CARDS — free-move collage (web only) ══ */}
        {isCollage && (
        <div
          ref={collageRef}
          className="relative mx-auto"
          style={{ width: LAYOUT_COLLAGE_WIDTH, height: collageHeight }}
        >

        {/* ══ OPENING REFLECTION ═══════════════════════════════════ */}
        <CollageCard
          label="Opening Reflection" labelNode={headingNode("openingReflection", "Opening Reflection")} accent={palette.accent} collage={isCollage}
          dataKey="openingReflection" pos={posFor("openingReflection")} onMove={(p) => moveCard("openingReflection", p)}
          zIndex={zIndexFor("openingReflection")} onFront={() => bringToFront("openingReflection")}
          overlay={anchoredStickersFor("openingReflection")} edgePad={edgePadFor("openingReflection")} anchorId="openingReflection" anchorLabel={SECTION_TITLES["openingReflection"]} dropHint={anchorHover === "openingReflection" ? `Attach to ${SECTION_TITLES["openingReflection"]}` : null}
          collapsed={collapsed.has("openingReflection")} onToggleCollapse={() => toggleCollapse("openingReflection")}
          selected={selectedSection === "openingReflection"} onSelect={() => setSelectedSection("openingReflection")}
          pill={<SourcePill sectionKey="openingReflection" label="Opening Reflection" />}
        >
          <RichText
            value={richValue("openingReflection", localOpeningReflection)}
            onChange={(h) => { setRichField("openingReflection", h, "openingReflection"); setLocalOpeningReflection(stripHtml(h)); }}
            onFocus={() => setSelectedSection("openingReflection")}
            placeholder="Opening reflection…"
            style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "16px", lineHeight: "28px", color: "#1c1917", ...secStyle("openingReflection", 16) }}
          />
        </CollageCard>

        {/* ══ CORE INSIGHT ════════════════════════════════════════ */}
        <CollageCard
          label="Core Insight" labelNode={headingNode("coreInsight", "Core Insight")} accent={palette.accent} collage={isCollage}
          dataKey="coreInsight" pos={posFor("coreInsight")} onMove={(p) => moveCard("coreInsight", p)}
          zIndex={zIndexFor("coreInsight")} onFront={() => bringToFront("coreInsight")}
          overlay={anchoredStickersFor("coreInsight")} edgePad={edgePadFor("coreInsight")} anchorId="coreInsight" anchorLabel={SECTION_TITLES["coreInsight"]} dropHint={anchorHover === "coreInsight" ? `Attach to ${SECTION_TITLES["coreInsight"]}` : null}
          collapsed={collapsed.has("coreInsight")} onToggleCollapse={() => toggleCollapse("coreInsight")}
          selected={selectedSection === "coreInsight"} onSelect={() => setSelectedSection("coreInsight")}
          pill={<SourcePill sectionKey="coreInsight" label="Core Insight" />}
        >
          <RichText
            value={richValue("coreInsight", localCoreInsight)}
            onChange={(h) => { setRichField("coreInsight", h, "coreInsight"); setLocalCoreInsight(stripHtml(h)); }}
            onFocus={() => setSelectedSection("coreInsight")}
            placeholder="Core insight…"
            style={{ fontFamily: "'Newsreader', serif", fontSize: "17px", fontStyle: "italic", lineHeight: "1.75", color: "#1c1917", ...secStyle("coreInsight", 17) }}
          />
        </CollageCard>

        {/* ══ WHAT SURFACED ═══════════════════════════════════════ */}
        <CollageCard
          label="What Surfaced" labelNode={headingNode("whatSurfaced", "What Surfaced")} accent={palette.accent} collage={isCollage}
          dataKey="whatSurfaced" pos={posFor("whatSurfaced")} onMove={(p) => moveCard("whatSurfaced", p)}
          zIndex={zIndexFor("whatSurfaced")} onFront={() => bringToFront("whatSurfaced")}
          overlay={anchoredStickersFor("whatSurfaced")} edgePad={edgePadFor("whatSurfaced")} anchorId="whatSurfaced" anchorLabel={SECTION_TITLES["whatSurfaced"]} dropHint={anchorHover === "whatSurfaced" ? `Attach to ${SECTION_TITLES["whatSurfaced"]}` : null}
          collapsed={collapsed.has("whatSurfaced")} onToggleCollapse={() => toggleCollapse("whatSurfaced")}
          selected={selectedSection === "whatSurfaced"} onSelect={() => setSelectedSection("whatSurfaced")}
          pill={<SourcePill sectionKey="whatSurfaced" label="What Surfaced" />}
        >
          <RichText
            value={richValue("whatSurfaced", localWhatSurfaced)}
            onChange={(h) => { setRichField("whatSurfaced", h, "whatSurfaced"); setLocalWhatSurfaced(stripHtml(h)); }}
            onFocus={() => setSelectedSection("whatSurfaced")}
            placeholder="What surfaced for you…"
            style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "16px", lineHeight: "28px", color: "#1c1917", ...secStyle("whatSurfaced", 16) }}
          />
        </CollageCard>

        {/* ══ SIT WITH THIS ═══════════════════════════════════════ */}
        <CollageCard
          label="Sit With This" labelNode={headingNode("reflectionPrompt", "Sit With This")} accent={palette.accent} collage={isCollage}
          dataKey="reflectionPrompt" pos={posFor("reflectionPrompt")} onMove={(p) => moveCard("reflectionPrompt", p)}
          zIndex={zIndexFor("reflectionPrompt")} onFront={() => bringToFront("reflectionPrompt")}
          overlay={anchoredStickersFor("reflectionPrompt")} edgePad={edgePadFor("reflectionPrompt")} anchorId="reflectionPrompt" anchorLabel={SECTION_TITLES["reflectionPrompt"]} dropHint={anchorHover === "reflectionPrompt" ? `Attach to ${SECTION_TITLES["reflectionPrompt"]}` : null}
          collapsed={collapsed.has("reflectionPrompt")} onToggleCollapse={() => toggleCollapse("reflectionPrompt")}
          selected={selectedSection === "reflectionPrompt"} onSelect={() => setSelectedSection("reflectionPrompt")}
          pill={<SourcePill sectionKey="reflectionPrompt" label="Sit With This" />}
        >
          <RichText
            value={richValue("reflectionPrompt", localReflectionPrompt)}
            onChange={(h) => { setRichField("reflectionPrompt", h, "reflectionPrompt"); setLocalReflectionPrompt(stripHtml(h)); }}
            onFocus={() => setSelectedSection("reflectionPrompt")}
            placeholder="Your reflection prompt…"
            className="text-stone-900 leading-relaxed w-full"
            style={{ fontFamily: "'Newsreader', serif", fontSize: "17px", fontStyle: "italic", lineHeight: "1.65", ...secStyle("reflectionPrompt", 17) }}
          />
          {/* User writing space */}
          <div onClick={(e) => e.stopPropagation()} className="mt-4 border-t border-[#e4ddd4] pt-3" style={{
            backgroundImage: "repeating-linear-gradient(to bottom, transparent, transparent 27px, #e8e3db 27px, #e8e3db 28px)",
            backgroundPositionY: "4px",
          }}>
            <RichText
              value={richValue("yourThoughts", "Your thoughts ↓")}
              onChange={(h) => setRichField("yourThoughts", h)}
              onFocus={() => setSelectedSection("yourThoughts")}
              singleLine
              className="text-[9px] text-stone-600 font-sans mb-1"
              style={secStyle("yourThoughts", 9)}
            />
            <div className="relative">
              <textarea
                value={freeWrite}
                onChange={(e) => { setFreeWrite(e.target.value); saveFreeWrite(e.target.value); }}
                placeholder="Write your reflection here…"
                rows={2}
                className="w-full bg-transparent text-stone-900 placeholder:text-stone-500 focus:outline-none resize-none leading-[26px] pr-10"
                style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize: "16px" }}
              />
              <VoiceMicButton onTranscript={(text) => { setFreeWrite((p) => { const v = p ? p + " " + text : text; saveFreeWrite(v); return v; }); }} />
            </div>
          </div>
        </CollageCard>
        </div>
        )}{/* /section cards collage (web only) */}

        {/* ══ NEXT STEPS — web: fixed "hero" (bottom-left, always open); mobile: FAB + sheet ══ */}
        {(() => {
          const nsBody = (
            <>
            <div className="flex-1 divide-y divide-[#f0ece6]">
              {localActions.map((action) => {
                const isDone = completedActions[action.id] ?? action.completed;
                const isExpanded = expandedActions.has(action.id);
                const noteVal = actionNotes[action.id] ?? "";
                const hasSubPrompts = action.subPrompts && action.subPrompts.length > 0;
                const isShortNote = action.subPrompts && action.subPrompts.length <= 1;
                return (
                  <div key={action.id} className="group/action py-2.5">
                    <div className="flex items-start gap-2">
                      <button onClick={() => {
                        setCompletedActions((p) => ({ ...p, [action.id]: !isDone }));
                        authFetch(apiUrl(`/journals/${params.id}/actions/${action.id}`), {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ completed: !isDone }),
                        }).catch(() => {});
                      }} className={cn("mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all",
                        isDone ? "border-stone-600" : "border-stone-500 hover:border-stone-700")}
                        style={isDone ? { background: palette.accent, borderColor: palette.accent } : {}}>
                        {isDone && (
                          <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none">
                            <path d="M2.5 6l2.5 2.5 4.5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <InlineEditText
                          value={action.text}
                          onChange={(v) => handleUpdateActionText(action.id, v)}
                          placeholder="Next step…"
                          className={cn("text-[15px] leading-relaxed font-sans w-full block",
                            isDone ? "line-through text-stone-400" : "text-stone-700")}
                        />
                        {hasSubPrompts && !isDone && (
                          <button
                            onClick={() => setExpandedActions((prev) => {
                              const next = new Set(prev);
                              next.has(action.id) ? next.delete(action.id) : next.add(action.id);
                              return next;
                            })}
                            className="mt-0.5 text-[9px] text-violet-600 hover:text-violet-800 transition-colors font-sans flex items-center gap-0.5"
                          >
                            <Pencil className="w-2 h-2" />
                            {isExpanded ? "close" : "write"}
                          </button>
                        )}
                      </div>
                      <button
                        className="opacity-0 group-hover/action:opacity-100 transition-opacity w-4 h-4 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center shrink-0"
                        onClick={() => handleDeleteAction(action.id)}
                      >
                        <X className="w-2 h-2 text-red-400" />
                      </button>
                    </div>
                    <AnimatePresence>
                      {isExpanded && hasSubPrompts && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className={cn("mt-2 pl-6", isShortNote ? "pb-1" : "space-y-2")}>
                            {isShortNote ? (
                              <div className="relative">
                                <textarea
                                  value={noteVal}
                                  onChange={(e) => handleActionNoteChange(action.id, e.target.value)}
                                  placeholder={action.subPrompts![0]}
                                  rows={2}
                                  className="w-full text-[12px] font-['Kalam'] text-stone-600 bg-transparent border-b border-[#e8e3db] focus:outline-none resize-none placeholder:text-stone-500 leading-relaxed pr-10"
                                />
                                <VoiceMicButton onTranscript={(text) => handleActionNoteChange(action.id, noteVal ? noteVal + " " + text : text)} />
                              </div>
                            ) : (
                              action.subPrompts!.map((prompt, pi) => (
                                <div key={pi} className="space-y-0.5">
                                  <p className="text-[9px] text-stone-600 font-sans uppercase tracking-wide">{prompt}</p>
                                  <div className="relative">
                                    <textarea
                                      value={(noteVal.split("|||")[pi]) ?? ""}
                                      onChange={(e) => {
                                        const parts = noteVal.split("|||");
                                        parts[pi] = e.target.value;
                                        handleActionNoteChange(action.id, parts.join("|||"));
                                      }}
                                      placeholder="Write here…"
                                      rows={2}
                                      className="w-full text-[12px] font-['Kalam'] text-stone-600 bg-transparent border-b border-[#e8e3db] focus:outline-none resize-none placeholder:text-stone-500 leading-relaxed pr-10"
                                    />
                                    <VoiceMicButton onTranscript={(text) => {
                                      const parts = noteVal.split("|||");
                                      parts[pi] = (parts[pi] ?? "") + (parts[pi] ? " " : "") + text;
                                      handleActionNoteChange(action.id, parts.join("|||"));
                                    }} />
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
            <button
              onClick={handleAddAction}
              className="mt-3 flex items-center gap-1 text-[10px] text-stone-600 hover:text-stone-800 transition-colors font-sans"
            >
              <Plus className="w-3 h-3" /> Add a step
            </button>
            </>
          );
          const heroHeader = (
            <div className="flex items-center gap-3 mb-4">
              <motion.span
                animate={{ rotate: [0, -6, 6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="w-12 h-12 rounded-2xl overflow-hidden shrink-0 shadow-md"
              >
                <img src="/images/UnfoldWin -logo.png" alt="UnfoldWin" className="w-full h-full object-cover object-left" />
              </motion.span>
              <div className="leading-tight">
                <h3 className="font-display font-semibold tracking-[-0.02em] text-[26px] text-stone-800 leading-none">Next Steps</h3>
                <p className="text-[12px] font-sans font-bold uppercase tracking-[0.16em] mt-1.5" style={{ color: palette.accent }}>Your momentum · your win</p>
              </div>
            </div>
          );
          const heroBg: React.CSSProperties = {
            background: `linear-gradient(158deg, ${palette.accent}2e, ${palette.accent}12 52%, rgba(255,255,255,0.94))`,
            border: `1.5px solid ${palette.accent}55`,
            backdropFilter: "blur(14px) saturate(170%)",
            WebkitBackdropFilter: "blur(14px) saturate(170%)",
          };
          if (isCollage) {
            // Collapsed → just the UnfoldWin logo; click to reopen.
            // Portalled to <body> so `fixed` anchors to the viewport (not a
            // transformed collage ancestor) and the card never gets clipped.
            if (nextStepsCollapsed) {
              return createPortal(
                <motion.button
                  onClick={() => setNextStepsCollapsed(false)}
                  whileTap={{ scale: 0.92 }}
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                  className="hero-glow fixed bottom-6 right-6 z-30 w-16 h-16 rounded-full overflow-hidden shadow-xl"
                  style={{ border: `2px solid ${palette.accent}55`, ["--hero-glow" as string]: `${palette.accent}33`, ["--hero-glow-strong" as string]: `${palette.accent}77` }}
                  title="Next Steps"
                  aria-label="Open Next Steps"
                >
                  <img src="/images/UnfoldWin -logo.png" alt="Next Steps" className="w-full h-full object-cover object-left" />
                </motion.button>,
                document.body,
              );
            }
            return createPortal(
              <motion.div
                // Re-keyed when Focus navigation reaches Next Steps, replaying the entrance as a cue.
                key={nextStepsPing}
                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: "spring", damping: 20, stiffness: 240 }}
                className="hero-glow fixed bottom-6 right-6 w-[360px] z-30 rounded-3xl p-6 pt-5 flex flex-col max-h-[calc(100vh-40px)] overflow-y-auto"
                style={{ ...heroBg, ["--hero-glow" as string]: `${palette.accent}2e`, ["--hero-glow-strong" as string]: `${palette.accent}66` }}
              >
                <button
                  onClick={() => setNextStepsCollapsed(true)}
                  title="Collapse"
                  aria-label="Collapse Next Steps"
                  className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-stone-500 hover:bg-black/5 transition-colors"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
                {heroHeader}
                {nsBody}
              </motion.div>,
              document.body,
            );
          }
          // Mobile: floating UnfoldWin logo (bottom-right) opens the Next Steps half-page sheet.
          return (
            <>
              <motion.button
                onClick={() => setNextStepsSheetOpen(true)}
                whileTap={{ scale: 0.92 }}
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                className="hero-glow fixed top-[68px] right-4 z-40 w-16 h-16 rounded-full overflow-hidden shadow-xl"
                style={{ border: `2px solid ${palette.accent}55`, ["--hero-glow" as string]: `${palette.accent}33`, ["--hero-glow-strong" as string]: `${palette.accent}77` }}
                title="Next Steps"
                aria-label="Open Next Steps"
              >
                <img src="/images/UnfoldWin -logo.png" alt="Next Steps" className="w-full h-full object-cover object-left" />
              </motion.button>
              {createPortal(
                <AnimatePresence>
                  {nextStepsSheetOpen && (
                    <div className="fixed inset-0 z-[55] flex items-end" onClick={() => setNextStepsSheetOpen(false)}>
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-stone-900/30 backdrop-blur-sm" />
                      <motion.div
                        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                        transition={{ type: "spring", damping: 32, stiffness: 360 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-h-[82vh] overflow-y-auto rounded-t-3xl p-6 pb-10"
                        style={{
                          background: "rgba(255,255,255,0.86)",
                          backdropFilter: "blur(44px) saturate(200%)",
                          WebkitBackdropFilter: "blur(44px) saturate(200%)",
                          borderTop: `4px solid ${palette.accent}`,
                          boxShadow: `0 -10px 46px ${palette.accent}44, 0 -4px 22px rgba(0,0,0,0.18)`,
                        }}
                      >
                        <div className="w-10 h-1 bg-stone-400/50 rounded-full mx-auto mb-4" />
                        <button onClick={() => setNextStepsSheetOpen(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/70 flex items-center justify-center text-stone-600 hover:bg-white">
                          <X className="w-4 h-4" />
                        </button>
                        {heroHeader}
                        {nsBody}
                      </motion.div>
                    </div>
                  )}
                </AnimatePresence>,
                document.body
              )}
            </>
          );
        })()}

      </div>{/* /journal content */}

      </div>
      );
      if (!isCollage) return world;
      return (
        <JournalCanvas
          ref={canvasRef}
          referenceWidth={LAYOUT_REFERENCE_WIDTH}
          getFocusTargets={getFocusTargets}
          getExtraBounds={getStrokesBounds}
          getInsets={getCanvasInsets}
          sketchActive={sketchActive}
          onViewSettled={sketchActive ? setSketchView : undefined}
          showPaper={!bgImage}
          className="flex-1 min-h-0 z-[1]"
        >
          {world}
        </JournalCanvas>
      );
      })()}{/* /relative journal canvas */}

      {/* ── Floating tool bar (draggable, liquid glass) ──────── */}
      <FloatingToolbar
        mobile={!isCollage}
        stepUndoAvailable={!!lastDeletedStep}
        onStepUndo={handleUndoDeleteStep}
        selectedLabel={selectedSection ? (SECTION_LABELS[selectedSection] ?? "Text") : null}
        font={selCur.font ?? "default"}
        onFontChange={(f) => updateSelectedSection({ font: f })}
        textColor={selCur.color ?? null}
        onTextColorChange={(c) => updateSelectedSection({ color: c })}
        scale={selCur.scale ?? 1}
        onScaleChange={(n) => updateSelectedSection({ scale: n })}
        onDeselect={() => setSelectedSection(null)}
        sketchActive={sketchActive}
        onToggleSketch={() => setSketchActive((v) => !v)}
        penColor={penColor}
        onPenColorChange={setPenColor}
        penSize={penSize}
        onPenSizeChange={setPenSize}
        eraser={eraser}
        onEraserChange={setEraser}
        onUndo={() => persistStrokes(strokes.slice(0, -1))}
        onClear={() => persistStrokes([])}
        hasStrokes={strokes.length > 0}
        bgImage={bgImage}
        onSetBackground={handleSetBackground}
        onOpenStickers={() => setShowStickerPanel(true)}
        onUploadImage={(dataUrl, name) => handleUploadSticker(dataUrl, name)}
        onSave={() => setLocation("/unfoldings")}
        saveState={saveState}
      />


      {/* ── Sticker picker panel ─────────────────────────────── */}
      <AnimatePresence>
        {showStickerPanel && (
          <StickerPanel
            onAdd={handleAddSticker}
            onClose={() => setShowStickerPanel(false)}
          />
        )}
      </AnimatePresence>

      {/* ── Attribution panel ────────────────────────────────── */}
      {attributionSection && (
        <AttributionPanel
          open={attributionOpen}
          onClose={() => setAttributionOpen(false)}
          sectionLabel={
            attributionSection === "openingReflection" ? "Opening Reflection" :
            attributionSection === "coreInsight" ? "Core Insight" :
            attributionSection === "whatSurfaced" ? "What Surfaced" :
            attributionSection === "reflectionPrompt" ? "Sit With This" :
            attributionSection === "pullQuote" ? "Pull Quote" :
            attributionSection === "actions" ? "Next Steps" :
            attributionSection
          }
          contributingQA={getContributingQA(attributionSection)}
          accentColor={palette.accent}
        />
      )}

      {/* ── Delete confirmation ──────────────────────────────── */}
      {showDeleteConfirm && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={() => setShowDeleteConfirm(false)}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <motion.div
            initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 28, stiffness: 340 }}
            className="relative w-full max-w-md bg-[#f7f3ec] rounded-t-3xl px-6 pt-6 pb-10 shadow-2xl border-t border-stone-200"
            onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-stone-300 mx-auto mb-5" />
            <div className="flex flex-col items-center text-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-display text-lg font-medium text-stone-800">Delete this entry?</h3>
                <p className="text-sm text-stone-400 mt-1 font-sans">This can't be undone. Your reflection will be gone.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <button onClick={() => deleteJournal.mutate({ id: params.id! })}
                disabled={deleteJournal.isPending}
                className="w-full py-3.5 rounded-full bg-red-500 text-white font-sans font-semibold text-sm hover:bg-red-600 transition-all disabled:opacity-50">
                {deleteJournal.isPending ? "Deleting…" : "Yes, delete it"}
              </button>
              <button onClick={() => setShowDeleteConfirm(false)}
                className="w-full py-3.5 rounded-full bg-stone-200 text-stone-600 font-sans font-medium text-sm hover:bg-stone-300 transition-all">
                Keep it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
